import React, { useState, useEffect } from 'react';
import { auth, db, storage, firebaseProjectId } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, query, where, setDoc, serverTimestamp, addDoc, deleteDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import TakeoverDetail from './TakeoverDetail';
import CampaignDetail from './CampaignDetail';
import { 
  FaEnvelope, FaSpinner, FaPaperPlane, FaComment, FaEye,
  FaDollarSign, FaTrophy, FaExclamationTriangle, FaFile,
  FaTasks, FaUsers, FaFileAlt, FaHandshake, FaStickyNote,
  FaCheckCircle, FaClock, FaEdit, FaTrash, FaDownload,
  FaTag, FaPaperclip, FaThumbtack, FaPlus, FaChartLine, FaBuilding
} from 'react-icons/fa';
import dayjs from 'dayjs';

const dspInstanceFirestoreConsoleUrl = (instanceId) => {
  const encodedId = encodeURIComponent(instanceId || '');
  return `https://console.firebase.google.com/project/${firebaseProjectId}/firestore/databases/-default-/data/~2FdspInstances~2F${encodedId}`;
};

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listTab, setListTab] = useState('home');
  const [userTab, setUserTab] = useState('info');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTakeovers, setUserTakeovers] = useState([]);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [selectedTakeover, setSelectedTakeover] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sortByBusiness, setSortByBusiness] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [dspDocLoading, setDspDocLoading] = useState(false);
  const [dspDocExists, setDspDocExists] = useState(false);
  const [dspEditorOpen, setDspEditorOpen] = useState(false);
  const [dspSaving, setDspSaving] = useState(false);
  const [dspLogoUploading, setDspLogoUploading] = useState(false);
  const [dspLinkUsersOpen, setDspLinkUsersOpen] = useState(false);
  const [dspLinkUserSearch, setDspLinkUserSearch] = useState('');
  const [dspCampaignsOpen, setDspCampaignsOpen] = useState(false);

  const [isEditingUser, setIsEditingUser] = useState(false);
  const [userDraft, setUserDraft] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    website: '',
    isBusinessAccount: false,
    isVIP: false,
    phoneVerified: false,
    orders: [],
    campaigns: [],
    // CRM Fields
    industry: '',
    companySize: '',
    dealStage: 'lead',
    dealValue: '',
    leadSource: '',
    accountManager: '',
    crmNotes: '',
    lastContactDate: null,
    nextFollowUpDate: null,
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    companyCountry: '',
    taxId: '',
    billingEmail: ''
  });
  const [savingUser, setSavingUser] = useState(false);
  const [newOrderId, setNewOrderId] = useState('');
  const [newCampaignId, setNewCampaignId] = useState('');
  
  // CRM Activity Log
  const [activityLog, setActivityLog] = useState([]);
  const [newActivity, setNewActivity] = useState('');
  const [addingActivity, setAddingActivity] = useState(false);
  
  // CRM Filters
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterDealStage, setFilterDealStage] = useState('');
  const [filterAccountManager, setFilterAccountManager] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortByDate, setSortByDate] = useState('newest');

  // Embed filter for All Users tab
  const [filterEmbedUsers, setFilterEmbedUsers] = useState('all');

  // Company Favicons
  const [companyFavicons, setCompanyFavicons] = useState({});

  // CRM - Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // CRM - Tasks
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskFilter, setTaskFilter] = useState('all');

  // CRM - Documents
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // CRM - Deals
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);

  // CRM - Enhanced Notes
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // CRM Dashboard Stats
  const [crmStats, setCrmStats] = useState({
    totalDeals: 0,
    dealsPipeline: 0,
    tasksOverdue: 0,
    documentsCount: 0
  });

  const [dspDraft, setDspDraft] = useState({
    adminUserId: '',
    billTerms: 'net_30',
    dspInstanceName: '',
    logoUrl: '',
    status: 'active',
    techFee: '',
    website: '',
    userEmail: '',
    userId: '',
    userName: '',
    blockAutoCreativeSubmission: false,
    hideTechFeeOnSignup: false
  });
  const [dspCampaignIdsDraft, setDspCampaignIdsDraft] = useState(new Set());
  const [dspLinkedUsersDraft, setDspLinkedUsersDraft] = useState(new Set());
  const [dspDealsDraftText, setDspDealsDraftText] = useState('');

  // New DSP Creation & Link Generation
  const [dspCreationModalOpen, setDspCreationModalOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [dspLinkModalOpen, setDspLinkModalOpen] = useState(false);
  const [generatedDspLink, setGeneratedDspLink] = useState('');
  const [generatedDspInstanceId, setGeneratedDspInstanceId] = useState('');
  const [newDspDraft, setNewDspDraft] = useState({
    dspInstanceName: '',
    logoUrl: '',
    techFee: '',
    website: '',
    billTerms: 'net_30',
    securityPin: '',
    hideTechFeeOnSignup: false
  });
  const [newDspLogoUploading, setNewDspLogoUploading] = useState(false);
  const [creatingDsp, setCreatingDsp] = useState(false);
  
  // Pending Invitations
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [editingInvitationId, setEditingInvitationId] = useState(null);
  const [editingExpiresAt, setEditingExpiresAt] = useState('');
  
  // All DSP Instances (including ones without users)
  const [allDspInstances, setAllDspInstances] = useState([]);
  const [dspInstancesLoading, setDspInstancesLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAllDspInstances();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const usersList = snap.docs.map(d => ({ 
        id: d.id, 
        createTime: d.createTime,
        ...d.data() 
      }));
      setUsers(usersList);
      
      // Load favicons for business accounts with websites
      await loadFaviconsForUsers(
        usersList.filter(u => u.isBusinessAccount && u.website)
      );
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAllDspInstances = async () => {
    setDspInstancesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'dspInstances'));
      const instances = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('Fetched all DSP instances:', instances);
      setAllDspInstances(instances);
    } catch (error) {
      console.error('Error fetching DSP instances:', error);
      setAllDspInstances([]);
    } finally {
      setDspInstancesLoading(false);
    }
  };

  const fetchFavicon = async (website) => {
    if (!website) return null;
    
    // Clean the website URL
    const cleanDomain = website.replace(/^https?:\/\//, '').split('/')[0];
    
    // Try Google's favicon service first (most reliable)
    const googleFaviconUrl = `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=64`;
    
    return googleFaviconUrl;
  };

  const loadFaviconsForUsers = async (userList) => {
    const faviconMap = {};
    
    for (const user of userList) {
      if (user.website && !companyFavicons[user.id]) {
        faviconMap[user.id] = await fetchFavicon(user.website);
      }
    }
    
    setCompanyFavicons(prev => ({ ...prev, ...faviconMap }));
  };

  const handleUserClick = (user) => {
    if (!user?.id) return;
    setSelectedUser(user);
    setUserTab('info');
    setIsEditingUser(false);
    setSelectedTakeover(null);
    setSelectedCampaign(null);
  };

  const loadActivityLog = async (userId) => {
    try {
      const activitySnap = await getDocs(
        query(collection(db, 'users', userId, 'activity'), 
        // orderBy would require an index, so we'll sort in memory
        )
      );
      const activities = activitySnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by timestamp descending
      activities.sort((a, b) => {
        const aTime = a.timestamp?.toMillis?.() || 0;
        const bTime = b.timestamp?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setActivityLog(activities);
    } catch (e) {
      console.error('Failed to load activity log:', e);
      setActivityLog([]);
    }
  };
  
  const handleAddActivity = async () => {
    if (!selectedUser || !newActivity.trim()) return;
    setAddingActivity(true);
    try {
      const activityData = {
        note: newActivity.trim(),
        timestamp: serverTimestamp(),
        createdBy: auth.currentUser?.email || auth.currentUser?.uid || 'unknown'
      };
      await addDoc(collection(db, 'users', selectedUser.id, 'activity'), activityData);
      setNewActivity('');
      await loadActivityLog(selectedUser.id);
    } catch (e) {
      console.error('Failed to add activity:', e);
      alert('Failed to add activity');
    } finally {
      setAddingActivity(false);
    }
  };

  // Auto-logging helper
  const logActivity = async (userId, action, details) => {
    try {
      await addDoc(collection(db, 'users', userId, 'activity'), {
        note: `${action}: ${details}`,
        timestamp: serverTimestamp(),
        createdBy: auth.currentUser?.email || auth.currentUser?.uid || 'system',
        type: 'auto'
      });
    } catch (e) {
      console.error('Failed to log activity:', e);
    }
  };

  // ========== CONTACTS CRUD ==========
  const fetchContacts = async (userId) => {
    setContactsLoading(true);
    try {
      const contactsSnap = await getDocs(collection(db, 'users', userId, 'contacts'));
      const contactsData = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      contactsData.sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return 0;
      });
      setContacts(contactsData);
    } catch (e) {
      console.error('Failed to load contacts:', e);
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const addContact = async (contactData) => {
    if (!selectedUser) return;
    try {
      await addDoc(collection(db, 'users', selectedUser.id, 'contacts'), {
        ...contactData,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || auth.currentUser?.uid
      });
      await logActivity(selectedUser.id, 'Contact Added', `${contactData.name} (${contactData.email})`);
      await fetchContacts(selectedUser.id);
      setShowContactModal(false);
      setEditingContact(null);
    } catch (e) {
      console.error('Failed to add contact:', e);
      alert('Failed to add contact');
    }
  };

  const updateContact = async (contactId, contactData) => {
    if (!selectedUser) return;
    try {
      await updateDoc(doc(db, 'users', selectedUser.id, 'contacts', contactId), {
        ...contactData,
        updatedAt: serverTimestamp()
      });
      await logActivity(selectedUser.id, 'Contact Updated', contactData.name);
      await fetchContacts(selectedUser.id);
      setShowContactModal(false);
      setEditingContact(null);
    } catch (e) {
      console.error('Failed to update contact:', e);
      alert('Failed to update contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!selectedUser || !window.confirm('Delete this contact?')) return;
    try {
      const contactRef = doc(db, 'users', selectedUser.id, 'contacts', contactId);
      const contactSnap = await getDoc(contactRef);
      const contactName = contactSnap.data()?.name || 'Contact';
      await deleteDoc(contactRef);
      await logActivity(selectedUser.id, 'Contact Deleted', contactName);
      await fetchContacts(selectedUser.id);
    } catch (e) {
      console.error('Failed to delete contact:', e);
      alert('Failed to delete contact');
    }
  };

  // ========== TASKS CRUD ==========
  const fetchTasks = async (userId) => {
    setTasksLoading(true);
    try {
      const tasksSnap = await getDocs(collection(db, 'users', userId, 'tasks'));
      const tasksData = tasksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      tasksData.sort((a, b) => {
        const aDate = a.dueDate?.toMillis?.() || a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const bDate = b.dueDate?.toMillis?.() || b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return aDate - bDate;
      });
      setTasks(tasksData);
    } catch (e) {
      console.error('Failed to load tasks:', e);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const addTask = async (taskData) => {
    if (!selectedUser) return;
    try {
      await addDoc(collection(db, 'users', selectedUser.id, 'tasks'), {
        ...taskData,
        status: taskData.status || 'pending',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || auth.currentUser?.uid
      });
      await logActivity(selectedUser.id, 'Task Created', `${taskData.title} - Due: ${taskData.dueDate ? dayjs(taskData.dueDate).format('MMM D, YYYY') : 'No date'}`);
      await fetchTasks(selectedUser.id);
      calculateCrmStats();
      setShowTaskModal(false);
      setEditingTask(null);
    } catch (e) {
      console.error('Failed to add task:', e);
      alert('Failed to add task');
    }
  };

  const updateTask = async (taskId, updates) => {
    if (!selectedUser) return;
    try {
      await updateDoc(doc(db, 'users', selectedUser.id, 'tasks', taskId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      await logActivity(selectedUser.id, 'Task Updated', updates.title || 'Task');
      await fetchTasks(selectedUser.id);
      calculateCrmStats();
      setShowTaskModal(false);
      setEditingTask(null);
    } catch (e) {
      console.error('Failed to update task:', e);
      alert('Failed to update task');
    }
  };

  const completeTask = async (taskId) => {
    if (!selectedUser) return;
    try {
      const taskRef = doc(db, 'users', selectedUser.id, 'tasks', taskId);
      const taskSnap = await getDoc(taskRef);
      const task = taskSnap.data();
      await updateDoc(taskRef, {
        status: 'completed',
        completedAt: serverTimestamp()
      });
      await logActivity(selectedUser.id, 'Task Completed', task?.title || 'Task');
      await fetchTasks(selectedUser.id);
      calculateCrmStats();
    } catch (e) {
      console.error('Failed to complete task:', e);
      alert('Failed to complete task');
    }
  };

  const deleteTask = async (taskId) => {
    if (!selectedUser || !window.confirm('Delete this task?')) return;
    try {
      const taskRef = doc(db, 'users', selectedUser.id, 'tasks', taskId);
      const taskSnap = await getDoc(taskRef);
      const taskTitle = taskSnap.data()?.title || 'Task';
      await deleteDoc(taskRef);
      await logActivity(selectedUser.id, 'Task Deleted', taskTitle);
      await fetchTasks(selectedUser.id);
      calculateCrmStats();
    } catch (e) {
      console.error('Failed to delete task:', e);
      alert('Failed to delete task');
    }
  };

  // ========== DOCUMENTS CRUD ==========
  const fetchDocuments = async (userId) => {
    setDocumentsLoading(true);
    try {
      const docsSnap = await getDocs(collection(db, 'users', userId, 'documents'));
      const docsData = docsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      docsData.sort((a, b) => {
        const aTime = a.uploadedAt?.toMillis?.() || 0;
        const bTime = b.uploadedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setDocuments(docsData);
    } catch (e) {
      console.error('Failed to load documents:', e);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const uploadDocument = async (file, metadata = {}) => {
    if (!selectedUser || !file) return;
    setUploadingDoc(true);
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const filePath = `user-documents/${selectedUser.id}/${fileName}`;
      const fileRef = storageRef(storage, filePath);
      
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      
      await addDoc(collection(db, 'users', selectedUser.id, 'documents'), {
        name: file.name,
        type: metadata.type || 'other',
        url: downloadURL,
        size: file.size,
        tags: metadata.tags || [],
        uploadedBy: auth.currentUser?.email || auth.currentUser?.uid,
        uploadedAt: serverTimestamp()
      });
      
      await logActivity(selectedUser.id, 'Document Uploaded', file.name);
      await fetchDocuments(selectedUser.id);
      calculateCrmStats();
    } catch (e) {
      console.error('Failed to upload document:', e);
      alert('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!selectedUser || !window.confirm('Delete this document?')) return;
    try {
      const docRef = doc(db, 'users', selectedUser.id, 'documents', docId);
      const docSnap = await getDoc(docRef);
      const docName = docSnap.data()?.name || 'Document';
      await deleteDoc(docRef);
      await logActivity(selectedUser.id, 'Document Deleted', docName);
      await fetchDocuments(selectedUser.id);
      calculateCrmStats();
    } catch (e) {
      console.error('Failed to delete document:', e);
      alert('Failed to delete document');
    }
  };

  // ========== DEALS CRUD ==========
  const fetchDeals = async (userId) => {
    setDealsLoading(true);
    try {
      const dealsSnap = await getDocs(collection(db, 'users', userId, 'deals'));
      const dealsData = dealsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDeals(dealsData);
    } catch (e) {
      console.error('Failed to load deals:', e);
      setDeals([]);
    } finally {
      setDealsLoading(false);
    }
  };

  const addDeal = async (dealData) => {
    if (!selectedUser) return;
    try {
      await addDoc(collection(db, 'users', selectedUser.id, 'deals'), {
        ...dealData,
        stage: dealData.stage || 'lead',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || auth.currentUser?.uid
      });
      await logActivity(selectedUser.id, 'Deal Created', `${dealData.name} - $${dealData.value}`);
      await fetchDeals(selectedUser.id);
      calculateCrmStats();
      setShowDealModal(false);
      setEditingDeal(null);
    } catch (e) {
      console.error('Failed to add deal:', e);
      alert('Failed to add deal');
    }
  };

  const updateDeal = async (dealId, updates) => {
    if (!selectedUser) return;
    try {
      await updateDoc(doc(db, 'users', selectedUser.id, 'deals', dealId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      await logActivity(selectedUser.id, 'Deal Updated', updates.name || 'Deal');
      await fetchDeals(selectedUser.id);
      calculateCrmStats();
      setShowDealModal(false);
      setEditingDeal(null);
    } catch (e) {
      console.error('Failed to update deal:', e);
      alert('Failed to update deal');
    }
  };

  const moveDealStage = async (dealId, newStage) => {
    if (!selectedUser) return;
    try {
      const dealRef = doc(db, 'users', selectedUser.id, 'deals', dealId);
      const dealSnap = await getDoc(dealRef);
      const deal = dealSnap.data();
      await updateDoc(dealRef, {
        stage: newStage,
        updatedAt: serverTimestamp()
      });
      await logActivity(selectedUser.id, 'Deal Stage Changed', `${deal?.name} moved to ${newStage}`);
      await fetchDeals(selectedUser.id);
      calculateCrmStats();
    } catch (e) {
      console.error('Failed to move deal stage:', e);
      alert('Failed to move deal stage');
    }
  };

  const deleteDeal = async (dealId) => {
    if (!selectedUser || !window.confirm('Delete this deal?')) return;
    try {
      const dealRef = doc(db, 'users', selectedUser.id, 'deals', dealId);
      const dealSnap = await getDoc(dealRef);
      const dealName = dealSnap.data()?.name || 'Deal';
      await deleteDoc(dealRef);
      await logActivity(selectedUser.id, 'Deal Deleted', dealName);
      await fetchDeals(selectedUser.id);
      calculateCrmStats();
    } catch (e) {
      console.error('Failed to delete deal:', e);
      alert('Failed to delete deal');
    }
  };

  // ========== NOTES CRUD ==========
  const fetchNotes = async (userId) => {
    setNotesLoading(true);
    try {
      const notesSnap = await getDocs(collection(db, 'users', userId, 'notes'));
      const notesData = notesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      notesData.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setNotes(notesData);
    } catch (e) {
      console.error('Failed to load notes:', e);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const addNote = async (noteData) => {
    if (!selectedUser) return;
    try {
      await addDoc(collection(db, 'users', selectedUser.id, 'notes'), {
        ...noteData,
        isPinned: false,
        attachments: noteData.attachments || [],
        tags: noteData.tags || [],
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || auth.currentUser?.uid
      });
      await logActivity(selectedUser.id, 'Note Added', noteData.content.substring(0, 50));
      await fetchNotes(selectedUser.id);
      setShowNoteModal(false);
      setEditingNote(null);
    } catch (e) {
      console.error('Failed to add note:', e);
      alert('Failed to add note');
    }
  };

  const updateNote = async (noteId, updates) => {
    if (!selectedUser) return;
    try {
      await updateDoc(doc(db, 'users', selectedUser.id, 'notes', noteId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      await fetchNotes(selectedUser.id);
      setShowNoteModal(false);
      setEditingNote(null);
    } catch (e) {
      console.error('Failed to update note:', e);
      alert('Failed to update note');
    }
  };

  const pinNote = async (noteId, isPinned) => {
    if (!selectedUser) return;
    try {
      await updateDoc(doc(db, 'users', selectedUser.id, 'notes', noteId), {
        isPinned: !isPinned
      });
      await fetchNotes(selectedUser.id);
    } catch (e) {
      console.error('Failed to pin note:', e);
      alert('Failed to pin note');
    }
  };

  const deleteNote = async (noteId) => {
    if (!selectedUser || !window.confirm('Delete this note?')) return;
    try {
      const noteRef = doc(db, 'users', selectedUser.id, 'notes', noteId);
      await deleteDoc(noteRef);
      await logActivity(selectedUser.id, 'Note Deleted', 'Note removed');
      await fetchNotes(selectedUser.id);
    } catch (e) {
      console.error('Failed to delete note:', e);
      alert('Failed to delete note');
    }
  };

  // ========== CRM STATS CALCULATOR ==========
  const calculateCrmStats = () => {
    if (!selectedUser) return;
    
    const totalDeals = deals.filter(d => d.stage !== 'closed-lost').length;
    const dealsPipeline = deals
      .filter(d => d.stage !== 'closed-lost' && d.stage !== 'closed-won')
      .reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
    
    const now = new Date();
    const tasksOverdue = tasks.filter(t => {
      if (t.status === 'completed') return false;
      const dueDate = t.dueDate?.toDate?.() || (t.dueDate ? new Date(t.dueDate) : null);
      return dueDate && dueDate < now;
    }).length;
    
    setCrmStats({
      totalDeals,
      dealsPipeline,
      tasksOverdue,
      documentsCount: documents.length
    });
  };

  // Helper: Get filtered tasks
  const getFilteredTasks = () => {
    const now = new Date();
    return tasks.filter(task => {
      if (taskFilter === 'all') return true;
      if (taskFilter === 'pending') return task.status === 'pending';
      if (taskFilter === 'completed') return task.status === 'completed';
      if (taskFilter === 'overdue') {
        if (task.status === 'completed') return false;
        const dueDate = task.dueDate?.toDate?.() || (task.dueDate ? new Date(task.dueDate) : null);
        return dueDate && dueDate < now;
      }
      return true;
    });
  };

  const handleSaveUserEdits = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const payload = {
        name: (userDraft.name || '').trim(),
        email: (userDraft.email || '').trim(),
        phoneNumber: (userDraft.phoneNumber || '').trim(),
        companyName: (userDraft.companyName || '').trim(),
        website: (userDraft.website || '').trim(),
        isBusinessAccount: Boolean(userDraft.isBusinessAccount),
        isVIP: Boolean(userDraft.isVIP),
        phoneVerified: Boolean(userDraft.phoneVerified),
        orders: Array.isArray(userDraft.orders) ? userDraft.orders : [],
        campaigns: Array.isArray(userDraft.campaigns) ? userDraft.campaigns : [],
        // CRM Fields
        industry: (userDraft.industry || '').trim(),
        companySize: (userDraft.companySize || '').trim(),
        dealStage: userDraft.dealStage || 'lead',
        dealValue: (userDraft.dealValue || '').trim(),
        leadSource: (userDraft.leadSource || '').trim(),
        accountManager: (userDraft.accountManager || '').trim(),
        crmNotes: (userDraft.crmNotes || '').trim(),
        lastContactDate: userDraft.lastContactDate,
        nextFollowUpDate: userDraft.nextFollowUpDate,
        companyAddress: (userDraft.companyAddress || '').trim(),
        companyCity: (userDraft.companyCity || '').trim(),
        companyState: (userDraft.companyState || '').trim(),
        companyZip: (userDraft.companyZip || '').trim(),
        companyCountry: (userDraft.companyCountry || '').trim(),
        taxId: (userDraft.taxId || '').trim(),
        billingEmail: (userDraft.billingEmail || '').trim(),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || auth.currentUser?.uid || 'unknown'
      };
      // Back-compat: some parts of the app may use `campaign` (singular) instead of `campaigns`
      if (Array.isArray(selectedUser?.campaign)) {
        payload.campaign = payload.campaigns;
      }
      await updateDoc(userRef, payload);

      setSelectedUser(prev => prev ? ({ ...prev, ...payload }) : prev);
      await fetchUsers();
      setIsEditingUser(false);
      alert('User updated.');
    } catch (e) {
      console.error('Failed to update user:', e);
      alert(`Failed to update user: ${e?.message || 'Unknown error'}`);
    } finally {
      setSavingUser(false);
    }
  };

  const uniq = (arr) => Array.from(new Set((arr || []).map(x => String(x).trim()).filter(Boolean)));
  const addOrderId = (id) => {
    const v = String(id || '').trim();
    if (!v) return;
    setUserDraft(p => ({ ...p, orders: uniq([...(p.orders || []), v]) }));
  };
  const removeOrderId = (id) => {
    const v = String(id || '').trim();
    setUserDraft(p => ({ ...p, orders: (p.orders || []).filter(x => x !== v) }));
  };
  const addCampaignId = (id) => {
    const v = String(id || '').trim();
    if (!v) return;
    setUserDraft(p => ({ ...p, campaigns: uniq([...(p.campaigns || []), v]) }));
  };
  const removeCampaignId = (id) => {
    const v = String(id || '').trim();
    setUserDraft(p => ({ ...p, campaigns: (p.campaigns || []).filter(x => x !== v) }));
  };

  const generateDspInstanceId = () => {
    const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
    const ts = Date.now().toString(36).toUpperCase();
    return `DSP-${ts}-${rand}`;
  };

  const loadDspInstanceForUser = async (user, campaigns = []) => {
    setDspDocLoading(true);
    setDspLinkUsersOpen(false);
    setDspLinkUserSearch('');
    setDspCampaignsOpen(false);
    setPendingInvitations([]);
    try {
      // Try to find the DSP instance - either by user.dspInstanceId or user.id
      const dspInstanceId = user.dspInstanceId || user.id;
      const dspRef = doc(db, 'dspInstances', dspInstanceId);
      const snap = await getDoc(dspRef);

      if (snap.exists()) {
        const data = snap.data() || {};
        setDspDocExists(true);
        setDspEditorOpen(true);
        setDspDraft({
          adminUserId: data.adminUserId || user.id,
          billTerms: data.billTerms || 'net_30',
          dspInstanceName: data.dspInstanceName || '',
          logoUrl: data.logoUrl || '',
          status: data.status || 'active',
          techFee: typeof data.techFee === 'number' ? String(data.techFee) : (data.techFee || ''),
          website: data.website || '',
          userEmail: data.userEmail || user.email || '',
          userId: data.userId || user.id,
          userName: data.userName || user.name || user.email || '',
          securityPin: data.securityPin || '',
          blockAutoCreativeSubmission: data.blockAutoCreativeSubmission === true,
          hideTechFeeOnSignup: data.hideTechFeeOnSignup === true
        });
        setDspCampaignIdsDraft(new Set(Array.isArray(data.campaigns) ? data.campaigns : []));
        setDspLinkedUsersDraft(new Set(Array.isArray(data.linkedUsers) ? data.linkedUsers : []));
        setDspDealsDraftText(Array.isArray(data.deals) ? data.deals.join('\n') : '');
        
        // Load pending invitations for this DSP instance
        await loadPendingInvitations(dspInstanceId);
      } else {
        // Not created yet - seed defaults but keep editor collapsed until user clicks "Create"
        setDspDocExists(false);
        setDspEditorOpen(false);
        setDspDraft({
          adminUserId: user.id,
          billTerms: 'net_30',
          dspInstanceName: user.companyName || user.name || '',
          logoUrl: '',
          status: 'active',
          techFee: '',
          website: '',
          userEmail: user.email || '',
          userId: user.id,
          userName: user.name || user.email || '',
          securityPin: '',
          blockAutoCreativeSubmission: false,
          hideTechFeeOnSignup: false
        });
        setDspLinkedUsersDraft(new Set());
        setDspDealsDraftText('');

        // Auto-add campaigns only if user is business account
        if (campaigns.length > 0 && user.isBusinessAccount && !user.dspInstance && !user.dspInstanceId) {
          setDspCampaignIdsDraft(new Set(campaigns.map(c => c.id)));
        } else {
          setDspCampaignIdsDraft(new Set());
        }
      }
    } catch (e) {
      console.error('Failed to load dspInstances doc:', e);
      // Keep UI usable even if load fails
      setDspDocExists(false);
      setDspEditorOpen(false);
    } finally {
      setDspDocLoading(false);
    }
  };

  const parseDeals = (text) => {
    return (text || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
  };
  
  // Load pending invitations for a DSP instance
  const loadPendingInvitations = async (dspInstanceId) => {
    if (!dspInstanceId) {
      console.log('No dspInstanceId provided, skipping invitation load');
      setPendingInvitations([]);
      return;
    }
    
    console.log('Loading pending invitations for DSP instance:', dspInstanceId);
    setInvitationsLoading(true);
    try {
      const url = `https://vue-analytics-api.fly.dev/api/dsp/invitations/${dspInstanceId}`;
      console.log('Fetching invitations from:', url);
      const response = await fetch(url);
      const result = await response.json();
      
      console.log('Invitations API response:', result);
      
      if (result.success) {
        console.log('Found', result.invitations?.length || 0, 'pending invitations');
        setPendingInvitations(result.invitations || []);
      } else {
        console.error('Error loading pending invitations:', result.error);
        setPendingInvitations([]);
      }
    } catch (error) {
      console.error('Error loading pending invitations:', error);
      setPendingInvitations([]);
    } finally {
      setInvitationsLoading(false);
    }
  };
  
  // Cancel an invitation
  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }
    
    try {
      const response = await fetch(`https://vue-analytics-api.fly.dev/api/dsp/cancel-invitation/${invitationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert('Invitation cancelled successfully');
        // Reload invitations
        if (selectedUser) {
          const dspInstanceId = selectedUser.dspInstanceId || selectedUser.id;
          await loadPendingInvitations(dspInstanceId);
        }
      } else {
        alert(`Failed to cancel invitation: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert(`Failed to cancel invitation: ${error?.message || 'Unknown error'}`);
    }
  };
  
  // Resend/remind an invitation
  const handleRemindInvitation = async (invitationId) => {
    try {
      const response = await fetch(`https://vue-analytics-api.fly.dev/api/dsp/remind-invitation/${invitationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert('Reminder sent successfully');
      } else {
        alert(`Failed to send reminder: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert(`Failed to send reminder: ${error?.message || 'Unknown error'}`);
    }
  };
  
  // Copy invitation link to clipboard
  const handleCopyInvitationLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      alert('Invitation link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy link:', err);
      alert('Failed to copy link to clipboard');
    });
  };
  
  // Update invitation expiration date
  const handleUpdateInvitationExpiration = async (invitationId, newExpiresAt) => {
    try {
      const response = await fetch(`https://vue-analytics-api.fly.dev/api/dsp/update-invitation-expiration/${invitationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ expiresAt: newExpiresAt })
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert('Expiration date updated successfully!');
        setEditingInvitationId(null);
        setEditingExpiresAt('');
        // Reload invitations for the current DSP instance
        if (selectedUser?.dspInstanceId || selectedUser?.id) {
          await loadPendingInvitations(selectedUser.dspInstanceId || selectedUser.id);
        }
      } else {
        alert(`Failed to update expiration: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating expiration:', error);
      alert(`Failed to update expiration: ${error?.message || 'Unknown error'}`);
    }
  };
  
  // Start editing invitation expiration
  const handleStartEditExpiration = (invitation) => {
    setEditingInvitationId(invitation.id);
    // Format the date for the input field (YYYY-MM-DD)
    const expiresAt = invitation.expiresAt ? new Date(invitation.expiresAt) : new Date(new Date(invitation.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
    setEditingExpiresAt(expiresAt.toISOString().split('T')[0]);
  };
  
  // Cancel editing
  const handleCancelEditExpiration = () => {
    setEditingInvitationId(null);
    setEditingExpiresAt('');
  };

  const handleCreateDspInstance = async () => {
    if (!selectedUser) return;
    setUserTab('info');
    setDspEditorOpen(true);
    if (!dspDraft.website) {
      // Encourage required fields but keep it non-blocking
      setTimeout(() => {
        // no-op; UI is now visible
      }, 0);
    }
  };

  const handleUploadDspLogo = async (file) => {
    if (!selectedUser || !file) return;
    setDspLogoUploading(true);
    try {
      const path = `dspLogos/${selectedUser.id}/${file.name}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      setDspDraft(prev => ({ ...prev, logoUrl: url }));
    } catch (e) {
      console.error('Failed to upload DSP logo:', e);
      alert(`Failed to upload logo: ${e?.message || 'Unknown error'}`);
    } finally {
      setDspLogoUploading(false);
    }
  };

  const handleToggleCampaign = (campaignId) => {
    setDspCampaignIdsDraft(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  };

  const handleToggleLinkedUser = (userId) => {
    setDspLinkedUsersDraft(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSaveDspInstanceDoc = async () => {
    if (!selectedUser) return;
    const name = (dspDraft.dspInstanceName || '').trim();
    const website = (dspDraft.website || '').trim();
    const billTerms = dspDraft.billTerms || 'net_30';
    const status = dspDraft.status || 'active';
    const techFeeNum = Number(dspDraft.techFee);

    if (!name) {
      alert('Please enter a DSP Instance Name.');
      return;
    }
    if (!website) {
      alert('Please enter a website.');
      return;
    }
    if (!['net_30', 'net_15', 'pre_pay'].includes(billTerms)) {
      alert('Please choose bill terms (net_30, net_15, pre_pay).');
      return;
    }
    if (!Number.isFinite(techFeeNum)) {
      alert('Please enter a valid Tech Fee number.');
      return;
    }

    setDspSaving(true);
    try {
      // Use the user's dspInstanceId if it exists, otherwise use user.id
      const dspInstanceId = selectedUser.dspInstanceId || selectedUser.id;
      const dspRef = doc(db, 'dspInstances', dspInstanceId);
      const isCreate = !dspDocExists;
      const payload = {
        adminUserId: selectedUser.id,
        billTerms,
        blockAutoCreativeSubmission: dspDraft.blockAutoCreativeSubmission === true,
        hideTechFeeOnSignup: dspDraft.hideTechFeeOnSignup === true,
        campaigns: Array.from(dspCampaignIdsDraft),
        deals: parseDeals(dspDealsDraftText),
        dspInstanceName: name,
        linkedUsers: Array.from(dspLinkedUsersDraft),
        logoUrl: (dspDraft.logoUrl || '').trim(),
        status,
        techFee: techFeeNum,
        updatedAt: serverTimestamp(),
        userEmail: selectedUser.email || '',
        userId: selectedUser.id,
        userName: selectedUser.name || selectedUser.email || selectedUser.id,
        website
      };
      
      // Preserve securityPin if it exists
      if (dspDraft.securityPin) {
        payload.securityPin = dspDraft.securityPin;
      }
      
      if (isCreate) payload.createdAt = serverTimestamp();

      await setDoc(dspRef, payload, { merge: true });

      // Update PRIMARY ADMIN user record with DSP flags/timestamps
      const userRef = doc(db, 'users', selectedUser.id);
      const userUpdate = {
        // Per requirement: once a DSP instance exists, flag stays true
        dspInstance: true,
        // Should always match the dspInstances document id
        dspInstanceId: dspInstanceId,
        dspInstanceUpdatedAt: new Date(),
        dspInstanceUpdatedBy: auth.currentUser?.email || auth.currentUser?.uid || 'unknown'
      };
      // Only stamp createdAt on first creation (don't overwrite later)
      if (isCreate) {
        userUpdate.dspInstanceCreatedAt = serverTimestamp();
      }
      await updateDoc(userRef, userUpdate);

      setSelectedUser(prev => prev ? ({
        ...prev,
        dspInstance: true,
        dspInstanceId: dspInstanceId
      }) : prev);

      setDspDocExists(true);
      await fetchUsers();
      alert('DSP instance saved.');
    } catch (e) {
      console.error('Failed to save dspInstances doc:', e);
      alert(`Failed to save DSP instance: ${e?.message || 'Unknown error'}`);
    } finally {
      setDspSaving(false);
    }
  };

  const handleDisableDspInstance = async () => {
    if (!selectedUser) return;
    const ok = window.confirm('Set this DSP instance status to inactive and remove DSP flag from the user?');
    if (!ok) return;
    setDspDraft(prev => ({ ...prev, status: 'inactive' }));
    setTimeout(() => {
      // Save after state update
      handleSaveDspInstanceDoc();
    }, 0);
  };

  const handleUploadNewDspLogo = async (file) => {
    if (!file) return;
    setNewDspLogoUploading(true);
    try {
      const timestamp = Date.now();
      const path = `dspLogos/new_${timestamp}/${file.name}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      setNewDspDraft(prev => ({ ...prev, logoUrl: url }));
    } catch (e) {
      console.error('Failed to upload DSP logo:', e);
      alert(`Failed to upload logo: ${e?.message || 'Unknown error'}`);
    } finally {
      setNewDspLogoUploading(false);
    }
  };

  const generateSecurityPin = () => {
    // Generate a 6-digit random PIN
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const handleCreateDspAndGenerateLink = async () => {
    const name = (newDspDraft.dspInstanceName || '').trim();
    const website = (newDspDraft.website || '').trim();
    const billTerms = newDspDraft.billTerms || 'net_30';
    const techFeeNum = Number(newDspDraft.techFee);

    // Validation
    if (!name) {
      alert('Please enter a DSP Instance Name (Agency Name).');
      return;
    }
    if (!website) {
      alert('Please enter a website.');
      return;
    }
    if (!['net_30', 'net_15', 'pre_pay'].includes(billTerms)) {
      alert('Please choose valid bill terms (net_30, net_15, pre_pay).');
      return;
    }
    if (!Number.isFinite(techFeeNum) || techFeeNum < 0) {
      alert('Please enter a valid Tech Fee percentage (e.g., 12.5).');
      return;
    }
    if (!newDspDraft.logoUrl) {
      alert('Please upload a logo for the DSP instance.');
      return;
    }

    setCreatingDsp(true);
    try {
      // Generate a unique DSP instance ID and security PIN
      const dspInstanceId = generateDspInstanceId();
      const securityPin = generateSecurityPin();
      const dspRef = doc(db, 'dspInstances', dspInstanceId);

      // Calculate expiration date (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      const payload = {
        dspInstanceName: name,
        logoUrl: newDspDraft.logoUrl,
        techFee: techFeeNum,
        website,
        billTerms,
        status: 'pending_signup',
        adminUserId: null,
        linkedUsers: [],
        campaigns: [],
        deals: [],
        signupLinkGenerated: true,
        signupLinkCreatedAt: serverTimestamp(),
        signupLinkExpiresAt: expiresAt,
        securityPin: securityPin,
        hideTechFeeOnSignup: newDspDraft.hideTechFeeOnSignup === true,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'unknown',
        updatedAt: serverTimestamp()
      };

      await setDoc(dspRef, payload);

      // Generate signup link
      const signupLink = `https://app.vuebillboards.com/dsp-signup/${dspInstanceId}`;
      
      // Store the link, instance ID, and PIN
      setGeneratedDspLink(signupLink);
      setGeneratedDspInstanceId(dspInstanceId);
      setNewDspDraft(prev => ({ ...prev, securityPin }));
      
      // Close creation modal and open link modal
      setDspCreationModalOpen(false);
      setDspLinkModalOpen(true);

      // Don't reset securityPin yet - we need it for the display modal
      
      await fetchUsers();
    } catch (e) {
      console.error('Failed to create DSP instance:', e);
      alert(`Failed to create DSP instance: ${e?.message || 'Unknown error'}`);
    } finally {
      setCreatingDsp(false);
    }
  };

  const handleCopyDspLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedDspLink);
      alert('Link copied to clipboard!');
    } catch (e) {
      console.error('Failed to copy link:', e);
      // Fallback: select the text
      const linkInput = document.getElementById('generated-dsp-link-input');
      if (linkInput) {
        linkInput.select();
        try {
          document.execCommand('copy');
          alert('Link copied to clipboard!');
        } catch (err) {
          alert('Please manually copy the link.');
        }
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const exportUsersToCSV = (type = 'all') => {
    let exportList = users;
    if (type === 'personal') exportList = users.filter(u => !u.isBusinessAccount);
    else if (type === 'business') exportList = users.filter(u => u.isBusinessAccount);

    const headers = [
      'Name', 'Email', 'Phone', 'Account Type', 'VIP',
      'Company', 'Website', 'Industry', 'Company Size',
      'Deal Stage', 'Deal Value', 'Lead Source', 'Account Manager',
      'Company Address', 'City', 'State', 'Zip', 'Country',
      'Tax ID', 'Billing Email', 'Created'
    ];

    const escape = (val) => {
      if (val == null) return '';
      const str = String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const rows = exportList.map(u => {
      const created = u.createTime?._seconds
        ? new Date(u.createTime._seconds * 1000).toLocaleDateString()
        : '';
      return [
        escape(u.name),
        escape(u.email),
        escape(u.phoneNumber),
        u.isBusinessAccount ? 'Business' : 'Personal',
        u.isVIP ? 'Yes' : 'No',
        escape(u.companyName),
        escape(u.website),
        escape(u.industry),
        escape(u.companySize),
        escape(u.dealStage),
        escape(u.dealValue),
        escape(u.leadSource),
        escape(u.accountManager),
        escape(u.companyAddress),
        escape(u.companyCity),
        escape(u.companyState),
        escape(u.companyZip),
        escape(u.companyCountry),
        escape(u.taxId),
        escape(u.billingEmail),
        escape(created)
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const label = type === 'personal' ? 'personal-users' : type === 'business' ? 'business-users' : 'all-users';
    link.href = url;
    link.download = `${label}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const businessUsers = users.filter(u => u.isBusinessAccount);
  const personalUsers = users.filter(u => !u.isBusinessAccount);
  const dspManagedUsers = users.filter(u => u.dspInstance);
  const embedUsers = users
    .filter(u => u.createdViaEmbed === true)
    .sort((a, b) => {
      const aTime = a.createTime?._seconds
        ?? (a.createdAt?.toMillis?.() ? a.createdAt.toMillis() / 1000 : null)
        ?? (a.createdAt?.seconds ?? 0);
      const bTime = b.createTime?._seconds
        ?? (b.createdAt?.toMillis?.() ? b.createdAt.toMillis() / 1000 : null)
        ?? (b.createdAt?.seconds ?? 0);
      return bTime - aTime; // newest first
    });
  
  // Group DSP users by their DSP instance ID
  const dspInstanceGroups = dspManagedUsers.reduce((acc, user) => {
    const instanceId = user.dspInstanceId || user.id;
    if (!acc[instanceId]) {
      acc[instanceId] = [];
    }
    acc[instanceId].push(user);
    return acc;
  }, {});
  
  // DSP instances without any users (pending signup)
  const pendingDspInstances = allDspInstances.filter(dsp => 
    !dsp.adminUserId || dsp.status === 'pending_signup'
  );
  
  const totalDspCount = Object.keys(dspInstanceGroups).length + pendingDspInstances.length;

  // Component for displaying pending DSP instances
  const PendingDspInstanceCard = ({ dspInstance, onLoadInvitations, onCancelInvitation, onRemindInvitation, onCopyLink, onRefresh }) => {
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const [editingInviteId, setEditingInviteId] = useState(null);
    const [editingInviteExpires, setEditingInviteExpires] = useState('');
    const [editingSignupExpires, setEditingSignupExpires] = useState(false);
    const [signupExpiresDate, setSignupExpiresDate] = useState('');
    const [editDraft, setEditDraft] = useState({
      dspInstanceName: dspInstance.dspInstanceName || '',
      website: dspInstance.website || '',
      techFee: dspInstance.techFee || '',
      billTerms: dspInstance.billTerms || 'net_30',
      logoUrl: dspInstance.logoUrl || '',
      hideTechFeeOnSignup: dspInstance.hideTechFeeOnSignup === true
    });
    
    useEffect(() => {
      if (expanded && dspInstance.id) {
        loadInvitesForInstance();
      }
    }, [expanded, dspInstance.id]);
    
    const loadInvitesForInstance = async () => {
      setLoading(true);
      try {
        const response = await fetch(`https://vue-analytics-api.fly.dev/api/dsp/invitations/${dspInstance.id}`);
        const result = await response.json();
        if (result.success) {
          setInvites(result.invitations || []);
        }
      } catch (error) {
        console.error('Error loading invitations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const handleStartEditInviteExpiration = (invite) => {
      setEditingInviteId(invite.id);
      const expiresAt = invite.expiresAt ? new Date(invite.expiresAt) : new Date(new Date(invite.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
      setEditingInviteExpires(expiresAt.toISOString().split('T')[0]);
    };
    
    const handleCancelEditInviteExpiration = () => {
      setEditingInviteId(null);
      setEditingInviteExpires('');
    };
    
    const handleUpdateInviteExpiration = async (inviteId, newExpiresAt) => {
      try {
        const response = await fetch(`https://vue-analytics-api.fly.dev/api/dsp/update-invitation-expiration/${inviteId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ expiresAt: newExpiresAt })
        });
        
        const result = await response.json();
        
        if (result.success) {
          alert('Expiration date updated successfully!');
          setEditingInviteId(null);
          setEditingInviteExpires('');
          await loadInvitesForInstance();
        } else {
          alert(`Failed to update expiration: ${result.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating expiration:', error);
        alert(`Failed to update expiration: ${error?.message || 'Unknown error'}`);
      }
    };
    
    const handleStartEditSignupExpiration = () => {
      setEditingSignupExpires(true);
      const expiresAt = dspInstance.signupLinkExpiresAt 
        ? (dspInstance.signupLinkExpiresAt.toDate ? dspInstance.signupLinkExpiresAt.toDate() : new Date(dspInstance.signupLinkExpiresAt))
        : new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000);
      setSignupExpiresDate(expiresAt.toISOString().split('T')[0]);
    };
    
    const handleCancelEditSignupExpiration = () => {
      setEditingSignupExpires(false);
      setSignupExpiresDate('');
    };
    
    const handleUpdateSignupExpiration = async () => {
      try {
        const dspRef = doc(db, 'dspInstances', dspInstance.id);
        await updateDoc(dspRef, {
          signupLinkExpiresAt: new Date(signupExpiresDate),
          updatedAt: serverTimestamp()
        });
        
        alert('Signup link expiration updated successfully!');
        setEditingSignupExpires(false);
        setSignupExpiresDate('');
        if (onRefresh) await onRefresh();
      } catch (error) {
        console.error('Error updating signup expiration:', error);
        alert(`Failed to update expiration: ${error?.message || 'Unknown error'}`);
      }
    };
    
    const handleSavePendingDsp = async () => {
      const name = (editDraft.dspInstanceName || '').trim();
      const website = (editDraft.website || '').trim();
      const billTerms = editDraft.billTerms || 'net_30';
      const techFeeNum = Number(editDraft.techFee);
      
      if (!name) {
        alert('Please enter a DSP Instance Name.');
        return;
      }
      if (!website) {
        alert('Please enter a website.');
        return;
      }
      if (!['net_30', 'net_15', 'pre_pay'].includes(billTerms)) {
        alert('Please choose valid bill terms.');
        return;
      }
      if (!Number.isFinite(techFeeNum)) {
        alert('Please enter a valid Tech Fee number.');
        return;
      }
      
      setSaving(true);
      try {
        const dspRef = doc(db, 'dspInstances', dspInstance.id);
        const payload = {
          dspInstanceName: name,
          website,
          techFee: techFeeNum,
          billTerms,
          logoUrl: editDraft.logoUrl,
          hideTechFeeOnSignup: editDraft.hideTechFeeOnSignup === true,
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(dspRef, payload);
        setIsEditing(false);
        alert('DSP instance updated successfully!');
        onRefresh(); // Refresh the list
      } catch (error) {
        console.error('Error updating DSP instance:', error);
        alert(`Failed to update: ${error?.message || 'Unknown error'}`);
      } finally {
        setSaving(false);
      }
    };
    
    const handleUploadLogo = async (file) => {
      if (!file) return;
      setLogoUploading(true);
      try {
        const timestamp = Date.now();
        const path = `dspLogos/pending_${dspInstance.id}/${timestamp}_${file.name}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, file);
        const url = await getDownloadURL(r);
        setEditDraft(prev => ({ ...prev, logoUrl: url }));
      } catch (error) {
        console.error('Error uploading logo:', error);
        alert(`Failed to upload logo: ${error?.message || 'Unknown error'}`);
      } finally {
        setLogoUploading(false);
      }
    };
    
    const signupLink = `https://app.vuebillboards.com/dsp-signup/${dspInstance.id}`;
    
    return (
      <div className="f-card" style={{ padding: 20, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #fbbf24' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16 }}>
          <div style={{ flex: 1 }}>
            {!isEditing ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#78350f' }}>
                    {dspInstance.dspInstanceName || 'Unnamed DSP'}
                  </h4>
                  <span style={{ background: '#fbbf24', color: '#78350f', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                    PENDING SIGNUP
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#92400e', fontFamily: 'monospace', marginBottom: 8 }}>
                  ID: {dspInstance.id}
                </div>
                {dspInstance.website && (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    Website: {dspInstance.website}
                  </div>
                )}
                {dspInstance.techFee && (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    Tech Fee: {dspInstance.techFee}%
                    {dspInstance.hideTechFeeOnSignup && (
                      <span style={{ marginLeft: 8, fontStyle: 'italic' }}>(hidden on signup)</span>
                    )}
                  </div>
                )}
                {dspInstance.billTerms && (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    Bill Terms: {dspInstance.billTerms}
                  </div>
                )}
                {dspInstance.logoUrl && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>
                    <a href={dspInstance.logoUrl} target="_blank" rel="noreferrer" style={{ color: '#d97706' }}>View Logo</a>
                  </div>
                )}
                {dspInstance.securityPin && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>
                    <strong>Security PIN:</strong> <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 4, fontWeight: 700, letterSpacing: '0.2em' }}>{dspInstance.securityPin}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(dspInstance.securityPin);
                        alert('PIN copied!');
                      }}
                      style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11, background: '#fff', border: '1px solid #d97706', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                )}
                {editingSignupExpires ? (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>Link Expires:</strong>
                    <input
                      type="date"
                      className="f-input"
                      value={signupExpiresDate}
                      onChange={(e) => setSignupExpiresDate(e.target.value)}
                      style={{ fontSize: 12, padding: '4px 8px', width: 140, background: '#fff' }}
                    />
                    <button
                      onClick={handleUpdateSignupExpiration}
                      style={{ padding: '4px 8px', fontSize: 11, background: '#10b981', color: '#fff', border: '1px solid #10b981', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEditSignupExpiration}
                      style={{ padding: '4px 8px', fontSize: 11, background: '#fff', border: '1px solid #d97706', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>
                    <strong>Link Expires:</strong> {(() => {
                      const expiresAt = dspInstance.signupLinkExpiresAt 
                        ? (dspInstance.signupLinkExpiresAt.toDate ? dspInstance.signupLinkExpiresAt.toDate() : new Date(dspInstance.signupLinkExpiresAt))
                        : null;
                      if (!expiresAt) return 'Not set';
                      const now = new Date();
                      const isExpired = expiresAt < now;
                      return (
                        <span style={{ color: isExpired ? '#dc2626' : '#92400e', fontWeight: isExpired ? 600 : 400 }}>
                          {expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {isExpired && ' (Expired)'}
                        </span>
                      );
                    })()}
                    <button
                      onClick={handleStartEditSignupExpiration}
                      style={{ 
                        marginLeft: 8,
                        padding: '2px 8px',
                        fontSize: 11,
                        background: 'none',
                        border: 'none',
                        color: '#3b82f6',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                      title="Edit expiration date"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', marginBottom: 4 }}>DSP Instance Name</div>
                  <input 
                    className="f-input" 
                    value={editDraft.dspInstanceName} 
                    onChange={e => setEditDraft(prev => ({ ...prev, dspInstanceName: e.target.value }))}
                    style={{ background: '#fff' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', marginBottom: 4 }}>Website</div>
                  <input 
                    className="f-input" 
                    value={editDraft.website} 
                    onChange={e => setEditDraft(prev => ({ ...prev, website: e.target.value }))}
                    style={{ background: '#fff' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', marginBottom: 4 }}>Tech Fee (%)</div>
                  <input 
                    className="f-input" 
                    type="number" 
                    value={editDraft.techFee} 
                    onChange={e => setEditDraft(prev => ({ ...prev, techFee: e.target.value }))}
                    style={{ background: '#fff' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', marginBottom: 4 }}>Bill Terms</div>
                  <select 
                    className="f-input" 
                    value={editDraft.billTerms} 
                    onChange={e => setEditDraft(prev => ({ ...prev, billTerms: e.target.value }))}
                    style={{ background: '#fff' }}
                  >
                    <option value="net_30">net_30</option>
                    <option value="net_15">net_15</option>
                    <option value="pre_pay">pre_pay</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', marginBottom: 4 }}>hideTechFeeOnSignup</div>
                  <select
                    className="f-input"
                    value={editDraft.hideTechFeeOnSignup ? 'true' : 'false'}
                    onChange={e => setEditDraft(prev => ({ ...prev, hideTechFeeOnSignup: e.target.value === 'true' }))}
                    style={{ background: '#fff' }}
                  >
                    <option value="false">false</option>
                    <option value="true">true</option>
                  </select>
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 4, lineHeight: 1.4 }}>
                    When true, the Tech Fee badge is hidden on the DSP signup page even if a tech fee is set.
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', marginBottom: 4 }}>Logo</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadLogo(f);
                      }}
                      disabled={logoUploading || saving}
                      style={{ fontSize: 12 }}
                    />
                    {logoUploading && <span style={{ fontSize: 12, color: '#92400e' }}><FaSpinner className="fa-spin" /> Uploading...</span>}
                    {editDraft.logoUrl && (
                      <a href={editDraft.logoUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#d97706' }}>
                        View current logo
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexDirection: 'column' }}>
            {!isEditing ? (
              <>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => setIsEditing(true)}
                  style={{ fontSize: 12, background: '#fff', borderColor: '#d97706', color: '#78350f', whiteSpace: 'nowrap' }}
                >
                  Edit DSP
                </button>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(signupLink);
                    alert('Signup link copied!');
                  }}
                  style={{ fontSize: 12, background: '#fff', borderColor: '#d97706', color: '#78350f', whiteSpace: 'nowrap' }}
                >
                  Copy Signup Link
                </button>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => {
                    setExpanded(!expanded);
                    if (!expanded) loadInvitesForInstance();
                  }}
                  style={{ fontSize: 12, background: '#fff', borderColor: '#d97706', color: '#78350f', whiteSpace: 'nowrap' }}
                >
                  {expanded ? 'Hide' : 'Show'} Invites ({invites.length})
                </button>
              </>
            ) : (
              <>
                <button
                  className="f-button"
                  onClick={handleSavePendingDsp}
                  disabled={saving || logoUploading}
                  style={{ fontSize: 12, background: '#16a34a', borderColor: '#16a34a', color: '#fff', whiteSpace: 'nowrap' }}
                >
                  {saving ? (<><FaSpinner className="fa-spin" /> Saving...</>) : 'Save Changes'}
                </button>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => {
                    setIsEditing(false);
                    setEditDraft({
                      dspInstanceName: dspInstance.dspInstanceName || '',
                      website: dspInstance.website || '',
                      techFee: dspInstance.techFee || '',
                      billTerms: dspInstance.billTerms || 'net_30',
                      logoUrl: dspInstance.logoUrl || '',
                      hideTechFeeOnSignup: dspInstance.hideTechFeeOnSignup === true
                    });
                  }}
                  disabled={saving || logoUploading}
                  style={{ fontSize: 12, background: '#fff', borderColor: '#d97706', color: '#78350f', whiteSpace: 'nowrap' }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
        
        {expanded && (
          <div style={{ borderTop: '2px solid #fbbf24', paddingTop: 16, marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h5 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#78350f', textTransform: 'uppercase' }}>
                Pending Invitations ({invites.length})
              </h5>
              {loading && <span style={{ fontSize: 12, color: '#92400e' }}><FaSpinner className="fa-spin" /> Loading...</span>}
            </div>
            
            {invites.length === 0 && !loading ? (
              <div style={{ fontSize: 13, color: '#92400e', fontStyle: 'italic' }}>
                No pending invitations
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invites.map(inv => (
                  <div key={inv.id} style={{ 
                    padding: 12, 
                    background: '#fff', 
                    border: '1px solid #fbbf24', 
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 10
                  }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#78350f' }}>
                        {inv.email}
                      </div>
                      <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
                        Invited: {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </div>
                      {editingInviteId === inv.id ? (
                        <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                          <div style={{ fontSize: 12, color: '#92400e' }}>Expires:</div>
                          <input
                            type="date"
                            className="f-input"
                            value={editingInviteExpires}
                            onChange={(e) => setEditingInviteExpires(e.target.value)}
                            style={{ fontSize: 12, padding: '4px 8px', width: 140 }}
                          />
                          <button
                            className="f-button f-button-secondary"
                            onClick={() => handleUpdateInviteExpiration(inv.id, editingInviteExpires)}
                            style={{ fontSize: 11, padding: '4px 8px', background: '#10b981', color: '#fff', borderColor: '#10b981' }}
                          >
                            Save
                          </button>
                          <button
                            className="f-button f-button-secondary"
                            onClick={handleCancelEditInviteExpiration}
                            style={{ fontSize: 11, padding: '4px 8px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#92400e', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                          Expires: {(() => {
                            const expiresAt = inv.expiresAt ? new Date(inv.expiresAt) : (inv.createdAt ? new Date(new Date(inv.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000) : null);
                            if (!expiresAt) return 'N/A';
                            const now = new Date();
                            const isExpired = expiresAt < now;
                            return (
                              <span style={{ color: isExpired ? '#dc2626' : '#92400e', fontWeight: isExpired ? 600 : 400 }}>
                                {expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {isExpired && ' (Expired)'}
                              </span>
                            );
                          })()}
                          <button
                            onClick={() => handleStartEditInviteExpiration(inv)}
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: '#3b82f6', 
                              fontSize: 11, 
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0
                            }}
                            title="Edit expiration date"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      {inv.invitedByEmail && (
                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                          By: {inv.invitedByEmail}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        className="f-button f-button-secondary"
                        onClick={() => onCopyLink(inv.invitationLink)}
                        style={{ fontSize: 12, padding: '6px 12px' }}
                        title="Copy invitation link"
                      >
                        Copy Link
                      </button>
                      <button
                        className="f-button f-button-secondary"
                        onClick={() => onRemindInvitation(inv.id)}
                        style={{ fontSize: 12, padding: '6px 12px' }}
                        title="Send reminder email"
                      >
                        <FaPaperPlane style={{ marginRight: 4 }} />
                        Remind
                      </button>
                      <button
                        className="f-button f-button-secondary"
                        onClick={async () => {
                          await onCancelInvitation(inv.id);
                          await loadInvitesForInstance();
                        }}
                        style={{ fontSize: 12, padding: '6px 12px', background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }}
                        title="Cancel this invitation"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ========== REUSABLE CRM COMPONENTS ==========
  
  // Stats Card Component
  const StatsCard = ({ title, value, icon: Icon, color }) => (
    <div className="f-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
        </div>
        <Icon style={{ fontSize: 24, color: color || 'var(--ms-primary)', opacity: 0.7 }} />
      </div>
    </div>
  );

  // Task Card Component
  const TaskCard = ({ task, onComplete, onEdit, onDelete }) => {
    const dueDate = task.dueDate?.toDate?.() || (task.dueDate ? new Date(task.dueDate) : null);
    const isOverdue = dueDate && dueDate < new Date() && task.status !== 'completed';
    
    return (
      <div style={{ 
        padding: 16, 
        border: '1px solid var(--ms-border-light)', 
        borderRadius: 8,
        borderLeft: task.status === 'completed' ? '4px solid #10b981' : isOverdue ? '4px solid #ef4444' : '4px solid var(--ms-primary)',
        background: task.status === 'completed' ? '#f0fdf4' : '#fff'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, textDecoration: task.status === 'completed' ? 'line-through' : 'none' }}>
                {task.title}
              </h4>
              {task.priority && (
                <span style={{ 
                  fontSize: 10, 
                  padding: '2px 6px', 
                  borderRadius: 4, 
                  background: task.priority === 'high' ? '#fee2e2' : task.priority === 'medium' ? '#fef3c7' : '#e5e7eb',
                  color: task.priority === 'high' ? '#991b1b' : task.priority === 'medium' ? '#92400e' : '#374151',
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}>
                  {task.priority}
                </span>
              )}
            </div>
            {task.description && (
              <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 8 }}>
                {task.description}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', display: 'flex', gap: 12 }}>
              {dueDate && (
                <span style={{ color: isOverdue ? '#ef4444' : 'inherit', fontWeight: isOverdue ? 600 : 400 }}>
                  <FaClock style={{ marginRight: 4 }} />
                  Due: {dayjs(dueDate).format('MMM D, YYYY')}
                  {isOverdue && ' ⚠️'}
                </span>
              )}
              {task.assignedTo && <span>Assigned: {task.assignedTo}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {task.status !== 'completed' && (
              <button 
                className="f-button f-button-secondary" 
                onClick={() => onComplete(task.id)}
                style={{ fontSize: 11, padding: '4px 8px' }}
                title="Mark as complete"
              >
                <FaCheckCircle />
              </button>
            )}
            <button 
              className="f-button f-button-secondary" 
              onClick={() => onEdit(task)}
              style={{ fontSize: 11, padding: '4px 8px' }}
            >
              <FaEdit />
            </button>
            <button 
              className="f-button f-button-secondary" 
              onClick={() => onDelete(task.id)}
              style={{ fontSize: 11, padding: '4px 8px', color: '#ef4444' }}
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Contact Card Component
  const ContactCard = ({ contact, isPrimary, onEdit, onDelete }) => (
    <div style={{ 
      padding: 16, 
      border: '1px solid var(--ms-border-light)', 
      borderRadius: 8,
      borderLeft: isPrimary ? '4px solid var(--ms-primary)' : '1px solid var(--ms-border-light)',
      background: '#fff'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{contact.name}</h4>
            {isPrimary && (
              <span style={{ 
                fontSize: 10, 
                padding: '2px 6px', 
                borderRadius: 4, 
                background: '#dbeafe',
                color: '#1e40af',
                fontWeight: 600
              }}>
                PRIMARY
              </span>
            )}
          </div>
          {contact.title && (
            <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>
              {contact.title}
            </div>
          )}
          <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {contact.email && (
              <div><FaEnvelope style={{ marginRight: 6, fontSize: 10 }} />{contact.email}</div>
            )}
            {contact.phone && (
              <div>📱 {contact.phone}</div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            className="f-button f-button-secondary" 
            onClick={() => onEdit(contact)}
            style={{ fontSize: 11, padding: '4px 8px' }}
          >
            <FaEdit />
          </button>
          <button 
            className="f-button f-button-secondary" 
            onClick={() => onDelete(contact.id)}
            style={{ fontSize: 11, padding: '4px 8px', color: '#ef4444' }}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );

  // Document Card Component
  const DocumentCard = ({ doc, onView, onDelete }) => {
    const fileSize = doc.size ? (doc.size / 1024).toFixed(1) + ' KB' : 'Unknown';
    const uploadDate = doc.uploadedAt?.toDate?.() || (doc.uploadedAt ? new Date(doc.uploadedAt) : null);
    
    return (
      <div style={{ 
        padding: 12, 
        border: '1px solid var(--ms-border-light)', 
        borderRadius: 6,
        background: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <FaFile style={{ fontSize: 24, color: 'var(--ms-primary)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{doc.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>
              {fileSize} • {uploadDate ? dayjs(uploadDate).format('MMM D, YYYY') : 'Unknown date'} • by {doc.uploadedBy || 'Unknown'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a 
            href={doc.url} 
            target="_blank" 
            rel="noreferrer" 
            className="f-button f-button-secondary"
            style={{ fontSize: 11, padding: '4px 8px', textDecoration: 'none' }}
          >
            <FaDownload />
          </a>
          <button 
            className="f-button f-button-secondary" 
            onClick={() => onDelete(doc.id)}
            style={{ fontSize: 11, padding: '4px 8px', color: '#ef4444' }}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    );
  };

  // Deal Card Component (for pipeline)
  const DealCard = ({ deal, onEdit, onMove }) => {
    const expectedClose = deal.expectedCloseDate?.toDate?.() || (deal.expectedCloseDate ? new Date(deal.expectedCloseDate) : null);
    
    return (
      <div style={{ 
        padding: 12, 
        border: '1px solid var(--ms-border-light)', 
        borderRadius: 6,
        background: '#fff',
        marginBottom: 8,
        cursor: 'pointer'
      }}
      onClick={() => onEdit(deal)}
      >
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{deal.name}</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ms-primary)', marginBottom: 6 }}>
          ${parseFloat(deal.value || 0).toLocaleString()}
        </div>
        {deal.probability && (
          <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>
            Probability: {deal.probability}%
          </div>
        )}
        {expectedClose && (
          <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>
            Expected: {dayjs(expectedClose).format('MMM D, YYYY')}
          </div>
        )}
      </div>
    );
  };

  // Note Card Component
  const NoteCard = ({ note, onPin, onEdit, onDelete }) => {
    const createdDate = note.createdAt?.toDate?.() || (note.createdAt ? new Date(note.createdAt) : null);
    
    return (
      <div style={{ 
        padding: 16, 
        border: '1px solid var(--ms-border-light)', 
        borderRadius: 8,
        background: note.isPinned ? '#fffbeb' : '#fff',
        borderLeft: note.isPinned ? '4px solid #fbbf24' : '1px solid var(--ms-border-light)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{note.content}</div>
            {note.tags && note.tags.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                {note.tags.map((tag, idx) => (
                  <span key={idx} style={{ 
                    fontSize: 10, 
                    padding: '2px 6px', 
                    borderRadius: 4, 
                    background: '#e5e7eb',
                    color: '#374151'
                  }}>
                    <FaTag style={{ marginRight: 2 }} />{tag}
                  </span>
                ))}
              </div>
            )}
            {note.attachments && note.attachments.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>
                <FaPaperclip /> {note.attachments.length} attachment(s)
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginTop: 8 }}>
              {createdDate ? dayjs(createdDate).format('MMM D, YYYY [at] h:mm A') : 'Unknown date'} • {note.createdBy || 'Unknown'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              className="f-button f-button-secondary" 
              onClick={() => onPin(note.id, note.isPinned)}
              style={{ fontSize: 11, padding: '4px 8px', color: note.isPinned ? '#fbbf24' : 'inherit' }}
              title={note.isPinned ? 'Unpin' : 'Pin'}
            >
              <FaThumbtack />
            </button>
            <button 
              className="f-button f-button-secondary" 
              onClick={() => onEdit(note)}
              style={{ fontSize: 11, padding: '4px 8px' }}
            >
              <FaEdit />
            </button>
            <button 
              className="f-button f-button-secondary" 
              onClick={() => onDelete(note.id)}
              style={{ fontSize: 11, padding: '4px 8px', color: '#ef4444' }}
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">Users</h1>
            <p className="f-subtitle mb-0">Manage and communicate with Vue users</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="f-button f-button-secondary" onClick={() => setSortByBusiness(!sortByBusiness)}>
              {sortByBusiness ? 'Sort: Business First' : 'Sort: All Users'}
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="f-button f-button-secondary"
                onClick={() => setExportDropdownOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FaDownload style={{ fontSize: 13 }} /> Export CSV
              </button>
              {exportDropdownOpen && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                    onClick={() => setExportDropdownOpen(false)}
                  />
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                    background: '#fff', border: '1px solid var(--ms-border-light)',
                    borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    minWidth: 190, zIndex: 100, overflow: 'hidden'
                  }}>
                    {[
                      { label: `All Users (${users.length})`, type: 'all' },
                      { label: `Personal Users (${personalUsers.length})`, type: 'personal' },
                      { label: `Business Users (${businessUsers.length})`, type: 'business' }
                    ].map(({ label, type }) => (
                      <button
                        key={type}
                        onClick={() => { exportUsersToCSV(type); setExportDropdownOpen(false); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '10px 16px', border: 'none', background: 'none',
                          cursor: 'pointer', fontSize: 14, color: 'var(--ms-text)',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--ms-bg-secondary, #f5f5f5)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '0 32px 32px 32px' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--ms-border-light)', marginBottom: 24 }}>
            {['home', 'business', 'dsp', 'embed'].map(t => (
              <button key={t} className={`tab-button ${listTab === t ? 'active' : ''}`} onClick={() => setListTab(t)} style={{ padding: '8px 16px', border: 'none', background: 'none', color: listTab === t ? 'var(--ms-primary)' : 'var(--ms-text-secondary)', fontWeight: listTab === t ? 600 : 400, borderBottom: listTab === t ? '2px solid var(--ms-primary)' : 'none', cursor: 'pointer', textTransform: 'capitalize' }}>
                {t === 'home' ? 'All Users' : t === 'business' ? `Business (${businessUsers.length})` : t === 'dsp' ? `DSP (${totalDspCount})` : `Embed (${embedUsers.length})`}
              </button>
            ))}
          </div>

          {listTab === 'home' && (
            <div className="f-card">
              <div style={{ marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" placeholder="Search by name, email, or ID..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="f-input" style={{ flex: 1, minWidth: 240, maxWidth: 400 }} />
                <select
                  className="f-input"
                  value={filterEmbedUsers}
                  onChange={(e) => setFilterEmbedUsers(e.target.value)}
                  style={{ width: 'auto', minWidth: 180 }}
                >
                  <option value="all">All Users ({users.length})</option>
                  <option value="embed">Embed Users ({embedUsers.length})</option>
                  <option value="non-embed">Non-Embed Users ({users.length - embedUsers.length})</option>
                </select>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="f-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} style={{cursor:'pointer'}}>Name</th>
                      <th onClick={() => handleSort('email')} style={{cursor:'pointer'}}>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="3" style={{textAlign:'center', padding:40}}><FaSpinner className="fa-spin" /> Loading users...</td></tr>
                    ) : (
                      users
                        .filter(u => {
                          if (searchQuery && !(u.name || u.email || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
                          if (filterEmbedUsers === 'embed' && !u.createdViaEmbed) return false;
                          if (filterEmbedUsers === 'non-embed' && u.createdViaEmbed) return false;
                          return true;
                        })
                        .map(u => (
                          <tr key={u.id} onClick={() => handleUserClick(u)} style={{cursor:'pointer'}}>
                            <td style={{ fontWeight: 600 }}>{u.name || '—'}</td>
                            <td>{u.email || '—'}</td>
                            <td>
                              <div style={{display:'flex', gap: 6, flexWrap: 'wrap'}}>
                                {u.isBusinessAccount && <span style={{background:'#dbeafe', color:'#1e40af', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600}}>Business</span>}
                                {u.isVIP && <span style={{background:'#fffbeb', color:'#92400e', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600}}>VIP</span>}
                                {u.createdViaEmbed && <span style={{background:'#f0fdf4', color:'#15803d', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600}}>Embed</span>}
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {listTab === 'business' && (
            <div className="f-card">
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Business Accounts CRM</h3>
                
                {/* CRM Filters */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <select 
                    className="f-input" 
                    value={filterDealStage} 
                    onChange={(e) => setFilterDealStage(e.target.value)}
                    style={{ width: 'auto', minWidth: 150 }}
                  >
                    <option value="">All Stages</option>
                    <option value="lead">Lead</option>
                    <option value="qualified">Qualified</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed-won">Closed Won</option>
                    <option value="closed-lost">Closed Lost</option>
                  </select>
                  
                  <select 
                    className="f-input" 
                    value={filterIndustry} 
                    onChange={(e) => setFilterIndustry(e.target.value)}
                    style={{ width: 'auto', minWidth: 150 }}
                  >
                    <option value="">All Industries</option>
                    <option value="advertising">Advertising</option>
                    <option value="automotive">Automotive</option>
                    <option value="real-estate">Real Estate</option>
                    <option value="retail">Retail</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="technology">Technology</option>
                    <option value="finance">Finance</option>
                    <option value="hospitality">Hospitality</option>
                    <option value="other">Other</option>
                  </select>
                  
                  <input 
                    type="text"
                    placeholder="Filter by Account Manager..."
                    className="f-input"
                    value={filterAccountManager}
                    onChange={(e) => setFilterAccountManager(e.target.value)}
                    style={{ width: 'auto', minWidth: 200 }}
                  />
                  
                  <input 
                    type="date"
                    placeholder="From Date..."
                    className="f-input"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    style={{ width: 'auto', minWidth: 160 }}
                  />
                  
                  <input 
                    type="date"
                    placeholder="To Date..."
                    className="f-input"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    style={{ width: 'auto', minWidth: 160 }}
                  />
                  
                  <select 
                    className="f-input" 
                    value={sortByDate} 
                    onChange={(e) => setSortByDate(e.target.value)}
                    style={{ width: 'auto', minWidth: 140 }}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                  
                  {(filterDealStage || filterIndustry || filterAccountManager || filterDateFrom || filterDateTo) && (
                    <button 
                      className="f-button f-button-secondary"
                      onClick={() => {
                        setFilterDealStage('');
                        setFilterIndustry('');
                        setFilterAccountManager('');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                      }}
                      style={{ fontSize: 12 }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table className="f-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Contact</th>
                      <th>Industry</th>
                      <th>Deal Stage</th>
                      <th>Deal Value</th>
                      <th>Account Manager</th>
                      <th>Next Follow-Up</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessUsers
                      .filter(u => {
                        if (filterDealStage && u.dealStage !== filterDealStage) return false;
                        if (filterIndustry && u.industry !== filterIndustry) return false;
                        if (filterAccountManager && (!u.accountManager || !u.accountManager.toLowerCase().includes(filterAccountManager.toLowerCase()))) return false;
                        
                        // Date range filter
                        if (filterDateFrom && u.createTime) {
                          const createDate = new Date(u.createTime._seconds * 1000);
                          if (createDate < new Date(filterDateFrom)) return false;
                        }
                        if (filterDateTo && u.createTime) {
                          const createDate = new Date(filterDateTo);
                          createDate.setHours(23, 59, 59, 999); // End of day
                          const userCreateDate = new Date(u.createTime._seconds * 1000);
                          if (userCreateDate > createDate) return false;
                        }
                        
                        return true;
                      })
                      .sort((a, b) => {
                        // Sort by creation date
                        if (!a.createTime || !b.createTime) return 0;
                        const aTime = a.createTime._seconds;
                        const bTime = b.createTime._seconds;
                        return sortByDate === 'newest' ? bTime - aTime : aTime - bTime;
                      })
                      .map(u => {
                        const nextFollowUp = u.nextFollowUpDate?.toDate?.() || (u.nextFollowUpDate ? new Date(u.nextFollowUpDate) : null);
                        const isOverdue = nextFollowUp && nextFollowUp < new Date();
                        
                        return (
                          <tr key={u.id} onClick={() => handleUserClick(u)} style={{cursor:'pointer'}}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {companyFavicons[u.id] ? (
                                  <img 
                                    src={companyFavicons[u.id]} 
                                    alt={`${u.companyName} logo`}
                                    style={{ width: 24, height: 24, borderRadius: 4 }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : u.website ? (
                                  <div style={{ 
                                    width: 24, 
                                    height: 24, 
                                    borderRadius: 4, 
                                    background: 'var(--ms-primary)', 
                                    color: 'white', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontSize: 10,
                                    fontWeight: 700
                                  }}>
                                    {(u.companyName || 'C')[0].toUpperCase()}
                                  </div>
                                ) : null}
                                <div>
                                  <div style={{ fontWeight: 600 }}>{u.companyName || '—'}</div>
                                  <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>{u.companySize || ''}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{u.name || '—'}</div>
                              <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>{u.email}</div>
                            </td>
                            <td>
                              <span style={{ textTransform: 'capitalize' }}>{u.industry || '—'}</span>
                            </td>
                            <td>
                              <span style={{
                                background: u.dealStage === 'closed-won' ? '#dcfce7' : 
                                           u.dealStage === 'closed-lost' ? '#fee2e2' :
                                           u.dealStage === 'negotiation' ? '#fef3c7' :
                                           u.dealStage === 'proposal' ? '#dbeafe' :
                                           '#f3f4f6',
                                color: u.dealStage === 'closed-won' ? '#166534' :
                                       u.dealStage === 'closed-lost' ? '#991b1b' :
                                       u.dealStage === 'negotiation' ? '#92400e' :
                                       u.dealStage === 'proposal' ? '#1e40af' :
                                       '#374151',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'capitalize'
                              }}>
                                {(u.dealStage || 'lead').replace('-', ' ')}
                              </span>
                            </td>
                            <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {u.dealValue ? `$${parseFloat(u.dealValue).toLocaleString()}` : '—'}
                            </td>
                            <td>{u.accountManager || '—'}</td>
                            <td>
                              {nextFollowUp ? (
                                <span style={{ 
                                  color: isOverdue ? '#dc2626' : 'inherit',
                                  fontWeight: isOverdue ? 600 : 400,
                                  fontSize: 12
                                }}>
                                  {dayjs(nextFollowUp).format('MMM D, YYYY')}
                                  {isOverdue && ' ⚠️'}
                                </span>
                              ) : '—'}
                            </td>
                            <td>
                              {u.phoneVerified && <span style={{background:'#dcfce7', color:'#166534', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600}}>✓ Verified</span>}
                              {u.isVIP && <span style={{background:'#fffbeb', color:'#92400e', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600, marginLeft: 4}}>VIP</span>}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {listTab === 'embed' && (
            <div className="f-card">
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Embed-Created Users</h3>
                <p style={{ fontSize: 13, color: 'var(--ms-text-secondary)', margin: 0 }}>
                  Users who signed up through an embedded map view. These accounts are created with <code>createdViaEmbed: true</code> and onboarding is skipped automatically.
                </p>
              </div>
              {embedUsers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ms-text-secondary)', fontSize: 14 }}>
                  No embed-created users found.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="f-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Embed ID</th>
                        <th>Publisher Owner ID</th>
                        <th>Onboarding</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {embedUsers.map(u => (
                        <tr key={u.id} onClick={() => handleUserClick(u)} style={{ cursor: 'pointer' }}>
                          <td style={{ fontWeight: 600 }}>{u.name || '—'}</td>
                          <td>{u.email || '—'}</td>
                          <td>
                            {u.embedId ? (
                              <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                {u.embedId}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            {u.publisherOwnerId ? (
                              <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                {u.publisherOwnerId}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            {u.onboardingComplete ? (
                              <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>Complete</span>
                            ) : (
                              <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>Pending</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                            {u.createTime?._seconds
                              ? dayjs(u.createTime._seconds * 1000).format('MMM D, YYYY')
                              : u.createdAt?.toDate
                              ? dayjs(u.createdAt.toDate()).format('MMM D, YYYY')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {listTab === 'dsp' && (
            <div>
              <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
                  DSP Instances ({totalDspCount})
                  {dspInstancesLoading && <span style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginLeft: 12 }}><FaSpinner className="fa-spin" /> Loading...</span>}
                </h3>
                <button
                  className="f-button"
                  onClick={() => {
                    setDspCreationModalOpen(true);
                    fetchAllDspInstances(); // Refresh DSP instances
                  }}
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', border: 'none' }}
                >
                  + Create DSP & Generate Link
                </button>
              </div>
              
              <div style={{ display: 'grid', gap: 16 }}>
                {Object.entries(dspInstanceGroups).map(([instanceId, instanceUsers]) => {
                  const adminUser = instanceUsers.find(u => u.id === instanceId) || instanceUsers[0];
                  return (
                    <div key={instanceId} className="f-card" style={{ padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                              {adminUser.companyName || adminUser.name || 'Unnamed DSP'}
                            </h4>
                            <span style={{ background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                              {instanceUsers.length} {instanceUsers.length === 1 ? 'user' : 'users'}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', fontFamily: 'monospace' }}>
                            ID: {instanceId}
                          </div>
                        </div>
                        <button
                          className="f-button f-button-secondary"
                          onClick={() => handleUserClick(adminUser)}
                          style={{ fontSize: 13 }}
                        >
                          Manage DSP
                        </button>
                      </div>
                      
                      <div style={{ borderTop: '1px solid var(--ms-border-light)', paddingTop: 12, marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 8 }}>
                          Users ({instanceUsers.length})
                        </div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {instanceUsers.map(u => (
                            <div 
                              key={u.id} 
                              style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderRadius: 6,
                                cursor: 'pointer'
                              }}
                              onClick={() => handleUserClick(u)}
                            >
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name || u.email}</div>
                                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{u.email}</div>
                              </div>
                              {u.id === instanceId && (
                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                  Admin
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Pending DSP Instances (no users yet) */}
                {pendingDspInstances.map((dsp) => (
                  <PendingDspInstanceCard
                    key={dsp.id}
                    dspInstance={dsp}
                    onLoadInvitations={loadPendingInvitations}
                    onCancelInvitation={handleCancelInvitation}
                    onRemindInvitation={handleRemindInvitation}
                    onCopyLink={handleCopyInvitationLink}
                    onRefresh={fetchAllDspInstances}
                  />
                ))}
                
                {Object.keys(dspInstanceGroups).length === 0 && pendingDspInstances.length === 0 && (
                  <div className="f-card" style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 14, color: 'var(--ms-text-secondary)' }}>
                      No DSP instances yet. Create one to get started!
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedUser && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10000, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 800, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
            <div style={{ padding: '24px 32px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {selectedUser.isBusinessAccount && selectedUser.website && companyFavicons[selectedUser.id] ? (
                  <img 
                    src={companyFavicons[selectedUser.id]} 
                    alt={`${selectedUser.companyName} logo`}
                    style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid var(--ms-border-light)' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--ms-primary)', color: '#fff', display: selectedUser.isBusinessAccount && selectedUser.website && companyFavicons[selectedUser.id] ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
                  {getInitials(selectedUser.name, selectedUser.email)}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{selectedUser.name || selectedUser.email}</h2>
                  <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>ID: {selectedUser.id}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {selectedUser.dspInstanceId ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: '#ede9fe', color: '#5b21b6', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                      DSP: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{selectedUser.dspInstanceId}</span>
                    </span>
                  </div>
                ) : (
                  <button
                    className="f-button"
                    onClick={handleCreateDspInstance}
                    disabled={dspDocLoading}
                    style={{ opacity: dspDocLoading ? 0.7 : 1 }}
                    title="Create a DSP instance for this user (stored in dspInstances)"
                  >
                    {dspDocLoading ? (<><FaSpinner className="fa-spin" /> Loading...</>) : 'Create DSP Instance'}
                  </button>
                )}
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setSelectedTakeover(null);
                      setSelectedCampaign(null);
                    }}
                    style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}
                  >
                    &times;
                  </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '32px' }}>
              <div style={{ display: 'flex', gap: 24, borderBottom: '1px solid var(--ms-border-light)', marginBottom: 24 }}>
                {['info', 'crm', 'tasks', 'contacts', 'documents', 'deals', 'notes', 'reporting', 'activity', 'takeovers', 'campaigns'].map(t => (
                  <button key={t} onClick={() => setUserTab(t)} style={{ padding: '8px 0', border: 'none', background: 'none', color: userTab === t ? 'var(--ms-primary)' : 'var(--ms-text-secondary)', fontWeight: userTab === t ? 600 : 400, borderBottom: userTab === t ? '2px solid var(--ms-primary)' : 'none', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
                ))}
              </div>
              {userTab === 'info' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                  <div>
                    <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 16 }}>Contact Info</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ fontSize: 14 }}><b>Email:</b> {selectedUser.email || '—'}</div>
                      <div style={{ fontSize: 14 }}><b>Phone:</b> {selectedUser.phoneNumber || '—'}</div>
                      <div style={{ fontSize: 14 }}><b>Company:</b> {selectedUser.companyName || '—'}</div>
                      <div style={{ fontSize: 14 }}>
                        <b>Website:</b> {selectedUser.website ? (
                          <a href={`https://${selectedUser.website.replace(/^https?:\/\//, '')}`} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             style={{ color: 'var(--ms-primary)', textDecoration: 'none' }}>
                            {selectedUser.website}
                          </a>
                        ) : '—'}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 16 }}>Status</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ fontSize: 14 }}><b>Business:</b> {formatBool(selectedUser.isBusinessAccount)}</div>
                      <div style={{ fontSize: 14 }}><b>VIP:</b> {formatBool(selectedUser.isVIP)}</div>
                      <div style={{ fontSize: 14 }}><b>DSP:</b> {selectedUser.dspInstanceId ? (<span style={{ fontWeight: 700 }}>{selectedUser.dspInstanceId}</span>) : (<span style={{ color: 'var(--ms-text-secondary)' }}>None</span>)}</div>
                      {selectedUser.createdViaEmbed && (
                        <div style={{ marginTop: 4, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
                            Created via Embed
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 13 }}>
                              <b>Embed ID:</b>{' '}
                              {selectedUser.embedId ? (
                                <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>{selectedUser.embedId}</span>
                              ) : <span style={{ color: 'var(--ms-text-secondary)' }}>—</span>}
                            </div>
                            <div style={{ fontSize: 13 }}>
                              <b>Publisher Owner ID:</b>{' '}
                              {selectedUser.publisherOwnerId ? (
                                <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>{selectedUser.publisherOwnerId}</span>
                              ) : <span style={{ color: 'var(--ms-text-secondary)' }}>—</span>}
                            </div>
                            <div style={{ fontSize: 13 }}>
                              <b>Onboarding:</b>{' '}
                              {selectedUser.onboardingComplete
                                ? <span style={{ color: '#15803d', fontWeight: 600 }}>Complete (auto-skipped)</span>
                                : <span style={{ color: '#92400e', fontWeight: 600 }}>Pending</span>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                    <div style={{ gridColumn: '1 / -1', marginTop: 6, paddingTop: 18, borderTop: '1px solid var(--ms-border-light)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', margin: 0 }}>User Information</h3>
                        <div style={{ display: 'flex', gap: 10 }}>
                          {!isEditingUser ? (
                            <button className="f-button f-button-secondary" onClick={() => setIsEditingUser(true)}>Edit User</button>
                          ) : (
                            <>
                              <button
                                className="f-button f-button-secondary"
                                onClick={() => {
                                  setIsEditingUser(false);
                                  setUserDraft({
                                    name: selectedUser?.name || '',
                                    email: selectedUser?.email || '',
                                    phoneNumber: selectedUser?.phoneNumber || '',
                                    companyName: selectedUser?.companyName || '',
                                    website: selectedUser?.website || '',
                                    isBusinessAccount: Boolean(selectedUser?.isBusinessAccount),
                                    isVIP: Boolean(selectedUser?.isVIP),
                                    phoneVerified: Boolean(selectedUser?.phoneVerified),
                                    orders: Array.isArray(selectedUser?.orders) ? selectedUser.orders : [],
                                    campaigns: Array.isArray(selectedUser?.campaigns) ? selectedUser.campaigns : (Array.isArray(selectedUser?.campaign) ? selectedUser.campaign : []),
                                    // CRM Fields
                                    industry: selectedUser?.industry || '',
                                    companySize: selectedUser?.companySize || '',
                                    dealStage: selectedUser?.dealStage || 'lead',
                                    dealValue: selectedUser?.dealValue || '',
                                    leadSource: selectedUser?.leadSource || '',
                                    accountManager: selectedUser?.accountManager || '',
                                    crmNotes: selectedUser?.crmNotes || '',
                                    lastContactDate: selectedUser?.lastContactDate || null,
                                    nextFollowUpDate: selectedUser?.nextFollowUpDate || null,
                                    companyAddress: selectedUser?.companyAddress || '',
                                    companyCity: selectedUser?.companyCity || '',
                                    companyState: selectedUser?.companyState || '',
                                    companyZip: selectedUser?.companyZip || '',
                                    companyCountry: selectedUser?.companyCountry || '',
                                    taxId: selectedUser?.taxId || '',
                                    billingEmail: selectedUser?.billingEmail || ''
                                  });
                                  setNewOrderId('');
                                  setNewCampaignId('');
                                }}
                                disabled={savingUser}
                              >
                                Cancel
                              </button>
                              <button className="f-button" onClick={handleSaveUserEdits} disabled={savingUser}>
                                {savingUser ? (<><FaSpinner className="fa-spin" /> Saving…</>) : 'Save User'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {isEditingUser && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Name</div>
                            <input className="f-input" value={userDraft.name} onChange={e => setUserDraft(p => ({ ...p, name: e.target.value }))} />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Email</div>
                            <input className="f-input" value={userDraft.email} onChange={e => setUserDraft(p => ({ ...p, email: e.target.value }))} />
                            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                              Note: this updates Firestore only (it does not change Firebase Auth email).
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Phone</div>
                            <input className="f-input" value={userDraft.phoneNumber} onChange={e => setUserDraft(p => ({ ...p, phoneNumber: e.target.value }))} />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Company</div>
                            <input className="f-input" value={userDraft.companyName} onChange={e => setUserDraft(p => ({ ...p, companyName: e.target.value }))} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Website</div>
                            <input className="f-input" value={userDraft.website} onChange={e => setUserDraft(p => ({ ...p, website: e.target.value }))} placeholder="e.g. elitemarketing.agency" />
                          </div>
                          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                              <input type="checkbox" checked={userDraft.isBusinessAccount} onChange={e => setUserDraft(p => ({ ...p, isBusinessAccount: e.target.checked }))} />
                              Business Account
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                              <input type="checkbox" checked={userDraft.isVIP} onChange={e => setUserDraft(p => ({ ...p, isVIP: e.target.checked }))} />
                              VIP
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                              <input type="checkbox" checked={userDraft.phoneVerified} onChange={e => setUserDraft(p => ({ ...p, phoneVerified: e.target.checked }))} />
                              Phone Verified
                            </label>
                          </div>

                          <div style={{ gridColumn: '1 / -1', paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>
                              Takeover IDs (Orders)
                            </div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                              <input
                                className="f-input"
                                style={{ minWidth: 260 }}
                                placeholder="Add takeover/order ID…"
                                value={newOrderId}
                                onChange={(e) => setNewOrderId(e.target.value)}
                              />
                              <button
                                className="f-button f-button-secondary"
                                type="button"
                                onClick={() => { addOrderId(newOrderId); setNewOrderId(''); }}
                              >
                                Add
                              </button>
                            </div>
                            {userTakeovers.length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Pick from this user’s takeovers:</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {userTakeovers.slice(0, 12).map(t => (
                                    <button
                                      key={t.id}
                                      type="button"
                                      className="f-button f-button-secondary"
                                      onClick={() => addOrderId(t.id)}
                                      style={{ padding: '4px 10px', fontSize: 12 }}
                                    >
                                      {t.id}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {(userDraft.orders || []).map(id => (
                                <span key={id} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 999, padding: '2px 10px', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <code style={{ fontSize: 11 }}>{id}</code>
                                  <button type="button" onClick={() => removeOrderId(id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ms-text-secondary)' }}>×</button>
                                </span>
                              ))}
                              {(userDraft.orders || []).length === 0 && (
                                <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>None</span>
                              )}
                            </div>
                          </div>

                          <div style={{ gridColumn: '1 / -1', paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>
                              Campaign IDs
                            </div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                              <input
                                className="f-input"
                                style={{ minWidth: 260 }}
                                placeholder="Add campaign ID…"
                                value={newCampaignId}
                                onChange={(e) => setNewCampaignId(e.target.value)}
                              />
                              <button
                                className="f-button f-button-secondary"
                                type="button"
                                onClick={() => { addCampaignId(newCampaignId); setNewCampaignId(''); }}
                              >
                                Add
                              </button>
                            </div>
                            {userCampaigns.length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Pick from this user’s campaigns:</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {userCampaigns.slice(0, 12).map(c => (
                                    <button
                                      key={c.id}
                                      type="button"
                                      className="f-button f-button-secondary"
                                      onClick={() => addCampaignId(c.id)}
                                      style={{ padding: '4px 10px', fontSize: 12 }}
                                    >
                                      {c.id}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {(userDraft.campaigns || []).map(id => (
                                <span key={id} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 999, padding: '2px 10px', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <code style={{ fontSize: 11 }}>{id}</code>
                                  <button type="button" onClick={() => removeCampaignId(id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ms-text-secondary)' }}>×</button>
                                </span>
                              ))}
                              {(userDraft.campaigns || []).length === 0 && (
                                <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>None</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ gridColumn: '1 / -1', marginTop: 6, paddingTop: 18, borderTop: '1px solid var(--ms-border-light)' }}>
                      <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 10 }}>DSP Instance</h3>
                      {dspDocLoading ? (
                        <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
                          <FaSpinner className="fa-spin" /> Loading DSP instance…
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                            <button className="f-button f-button-secondary" onClick={() => setDspEditorOpen(v => !v)}>
                              {dspEditorOpen ? 'Hide DSP Editor' : (dspDocExists ? 'Edit DSP Instance' : 'Create DSP Instance')}
                            </button>
                            {dspDocExists && (
                              <button className="f-button f-button-secondary" onClick={handleDisableDspInstance} disabled={dspSaving}>
                                Set Inactive
                              </button>
                            )}
                            {(dspDocExists || selectedUser.dspInstanceId) && (
                              <a
                                className="f-button f-button-secondary"
                                href={dspInstanceFirestoreConsoleUrl(selectedUser.dspInstanceId || selectedUser.id)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: 'none' }}
                              >
                                Open in Firebase
                              </a>
                            )}
                          </div>

                          {dspEditorOpen && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Primary Admin</div>
                                <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
                                  <div><b>User ID:</b> {selectedUser.id}</div>
                                  <div><b>Email:</b> {selectedUser.email || '—'}</div>
                                  <div><b>Name:</b> {selectedUser.name || '—'}</div>
                                </div>
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>DSP Instance Name</div>
                                <input className="f-input" value={dspDraft.dspInstanceName} onChange={e => setDspDraft(prev => ({ ...prev, dspInstanceName: e.target.value }))} placeholder="e.g. Elite Marketing Agency" />
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Website</div>
                                <input className="f-input" value={dspDraft.website} onChange={e => setDspDraft(prev => ({ ...prev, website: e.target.value }))} placeholder="e.g. elitemarketing.agency" />
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Bill Terms</div>
                                <select className="f-input" value={dspDraft.billTerms} onChange={e => setDspDraft(prev => ({ ...prev, billTerms: e.target.value }))}>
                                  <option value="net_30">net_30</option>
                                  <option value="net_15">net_15</option>
                                  <option value="pre_pay">pre_pay</option>
                                </select>
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Tech Fee</div>
                                <input className="f-input" type="number" value={dspDraft.techFee} onChange={e => setDspDraft(prev => ({ ...prev, techFee: e.target.value }))} placeholder="e.g. 12.5" />
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>hideTechFeeOnSignup</div>
                                <select
                                  className="f-input"
                                  value={dspDraft.hideTechFeeOnSignup ? 'true' : 'false'}
                                  onChange={e => setDspDraft(prev => ({ ...prev, hideTechFeeOnSignup: e.target.value === 'true' }))}
                                >
                                  <option value="false">false</option>
                                  <option value="true">true</option>
                                </select>
                                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
                                  When true, the Tech Fee badge is hidden on the DSP signup page even if a tech fee is set.
                                </div>
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Status</div>
                                <select className="f-input" value={dspDraft.status} onChange={e => setDspDraft(prev => ({ ...prev, status: e.target.value }))}>
                                  <option value="active">active</option>
                                  <option value="inactive">inactive</option>
                                </select>
                              </div>

                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>blockAutoCreativeSubmission</div>
                                <select
                                  className="f-input"
                                  value={dspDraft.blockAutoCreativeSubmission ? 'true' : 'false'}
                                  onChange={e => setDspDraft(prev => ({ ...prev, blockAutoCreativeSubmission: e.target.value === 'true' }))}
                                >
                                  <option value="false">false</option>
                                  <option value="true">true</option>
                                </select>
                                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
                                  When true, users on this instance see internal-approval messaging and must manually submit creatives (same as the default Vue DSP instance).
                                </div>
                              </div>

                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Logo</div>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) handleUploadDspLogo(f);
                                    }}
                                    disabled={dspLogoUploading || dspSaving}
                                  />
                                  {dspLogoUploading && <span style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}><FaSpinner className="fa-spin" /> Uploading…</span>}
                                  {dspDraft.logoUrl && (
                                    <a href={dspDraft.logoUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                                      View current logo
                                    </a>
                                  )}
                                </div>
                              </div>

                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase' }}>
                                    Campaigns ({dspCampaignIdsDraft.size})
                                  </div>
                                  <button className="f-button f-button-secondary" onClick={() => setDspCampaignsOpen(v => !v)}>
                                    {dspCampaignsOpen ? 'Hide' : 'Select'}
                                  </button>
                                </div>
                                {dspCampaignsOpen && (
                                  <div style={{ border: '1px solid var(--ms-border-light)', padding: 10, borderRadius: 6, maxHeight: 220, overflowY: 'auto' }}>
                                    {userCampaigns.length === 0 ? (
                                      <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>No campaigns found for this user.</div>
                                    ) : (
                                      userCampaigns.map(c => (
                                        <label key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', fontSize: 14 }}>
                                          <input type="checkbox" checked={dspCampaignIdsDraft.has(c.id)} onChange={() => handleToggleCampaign(c.id)} />
                                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontWeight: 600 }}>{c.name || c.id}</span>
                                            <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{c.status || '—'}</span>
                                          </div>
                                        </label>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>

                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase' }}>
                                    Linked Users ({dspLinkedUsersDraft.size})
                                  </div>
                                  <button className="f-button f-button-secondary" onClick={() => setDspLinkUsersOpen(true)}>
                                    Link Users
                                  </button>
                                </div>
                                {dspLinkedUsersDraft.size > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {Array.from(dspLinkedUsersDraft).slice(0, 8).map(uid => (
                                      <span key={uid} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 999, padding: '2px 10px', fontSize: 12 }}>
                                        {uid}
                                      </span>
                                    ))}
                                    {dspLinkedUsersDraft.size > 8 && (
                                      <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>+{dspLinkedUsersDraft.size - 8} more</span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Pending Invitations */}
                              {dspDocExists && (
                                <div style={{ gridColumn: '1 / -1', paddingTop: 16, marginTop: 16, borderTop: '1px solid var(--ms-border-light)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase' }}>
                                      Pending Invitations ({pendingInvitations.length})
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                      <button
                                        className="f-button f-button-secondary"
                                        onClick={async () => {
                                          const dspId = selectedUser.dspInstanceId || selectedUser.id;
                                          await loadPendingInvitations(dspId);
                                        }}
                                        disabled={invitationsLoading}
                                        style={{ fontSize: 12, padding: '4px 10px' }}
                                      >
                                        {invitationsLoading ? (<><FaSpinner className="fa-spin" /> Loading...</>) : 'Refresh'}
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {pendingInvitations.length === 0 && !invitationsLoading ? (
                                    <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', fontStyle: 'italic' }}>
                                      No pending invitations
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                      {pendingInvitations.map(inv => (
                                        <div key={inv.id} style={{ 
                                          padding: 12, 
                                          background: '#fef3c7', 
                                          border: '1px solid #fbbf24', 
                                          borderRadius: 8,
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          flexWrap: 'wrap',
                                          gap: 10
                                        }}>
                                          <div style={{ flex: 1, minWidth: 200 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#78350f' }}>
                                              {inv.email}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
                                              Invited: {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                                            </div>
                                            {editingInvitationId === inv.id ? (
                                              <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                                                <div style={{ fontSize: 12, color: '#92400e' }}>Expires:</div>
                                                <input
                                                  type="date"
                                                  className="f-input"
                                                  value={editingExpiresAt}
                                                  onChange={(e) => setEditingExpiresAt(e.target.value)}
                                                  style={{ fontSize: 12, padding: '4px 8px', width: 140 }}
                                                />
                                                <button
                                                  className="f-button f-button-secondary"
                                                  onClick={() => handleUpdateInvitationExpiration(inv.id, editingExpiresAt)}
                                                  style={{ fontSize: 11, padding: '4px 8px', background: '#10b981', color: '#fff', borderColor: '#10b981' }}
                                                >
                                                  Save
                                                </button>
                                                <button
                                                  className="f-button f-button-secondary"
                                                  onClick={handleCancelEditExpiration}
                                                  style={{ fontSize: 11, padding: '4px 8px' }}
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            ) : (
                                              <div style={{ fontSize: 12, color: '#92400e', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                Expires: {(() => {
                                                  const expiresAt = inv.expiresAt ? new Date(inv.expiresAt) : (inv.createdAt ? new Date(new Date(inv.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000) : null);
                                                  if (!expiresAt) return 'N/A';
                                                  const now = new Date();
                                                  const isExpired = expiresAt < now;
                                                  return (
                                                    <span style={{ color: isExpired ? '#dc2626' : '#92400e', fontWeight: isExpired ? 600 : 400 }}>
                                                      {expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                      {isExpired && ' (Expired)'}
                                                    </span>
                                                  );
                                                })()}
                                                <button
                                                  onClick={() => handleStartEditExpiration(inv)}
                                                  style={{ 
                                                    background: 'none', 
                                                    border: 'none', 
                                                    color: '#3b82f6', 
                                                    fontSize: 11, 
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    padding: 0
                                                  }}
                                                  title="Edit expiration date"
                                                >
                                                  Edit
                                                </button>
                                              </div>
                                            )}
                                            {inv.invitedByEmail && (
                                              <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                                                By: {inv.invitedByEmail}
                                              </div>
                                            )}
                                          </div>
                                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <button
                                              className="f-button f-button-secondary"
                                              onClick={() => handleCopyInvitationLink(inv.invitationLink)}
                                              style={{ fontSize: 12, padding: '6px 12px' }}
                                              title="Copy invitation link"
                                            >
                                              Copy Link
                                            </button>
                                            <button
                                              className="f-button f-button-secondary"
                                              onClick={() => handleRemindInvitation(inv.id)}
                                              style={{ fontSize: 12, padding: '6px 12px' }}
                                              title="Send reminder email"
                                            >
                                              <FaPaperPlane style={{ marginRight: 4 }} />
                                              Remind
                                            </button>
                                            <button
                                              className="f-button f-button-secondary"
                                              onClick={() => handleCancelInvitation(inv.id)}
                                              style={{ fontSize: 12, padding: '6px 12px', background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }}
                                              title="Cancel this invitation"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Deals</div>
                                <textarea
                                  className="f-input"
                                  rows={4}
                                  placeholder="One deal per line"
                                  value={dspDealsDraftText}
                                  onChange={e => setDspDealsDraftText(e.target.value)}
                                />
                              </div>

                              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center' }}>
                                <button className="f-button" onClick={handleSaveDspInstanceDoc} disabled={dspSaving || dspLogoUploading}>
                                  {dspSaving ? (<><FaSpinner className="fa-spin" /> Saving…</>) : (dspDocExists ? 'Save DSP Instance' : 'Create DSP Instance')}
                                </button>
                                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                                  Stored in{' '}
                                  <a
                                    href={dspInstanceFirestoreConsoleUrl(selectedUser.dspInstanceId || selectedUser.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <code>dspInstances/{selectedUser.dspInstanceId || selectedUser.id}</code>
                                  </a>
                                </div>
                              </div>
                              
                              {dspDraft.securityPin && (
                                <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: 12, background: '#dbeafe', border: '1px solid #60a5fa', borderRadius: 8 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>Security PIN</div>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: '#1e40af', letterSpacing: '0.2em' }}>
                                      {dspDraft.securityPin}
                                    </code>
                                    <button
                                      className="f-button f-button-secondary"
                                      onClick={() => {
                                        navigator.clipboard.writeText(dspDraft.securityPin);
                                        alert('PIN copied!');
                                      }}
                                      style={{ fontSize: 12 }}
                                    >
                                      Copy PIN
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                </div>
              )}
              {userTab === 'crm' && selectedUser.isBusinessAccount && (
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>CRM Overview</h3>
                  
                  {/* Stats Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                    <StatsCard title="Open Deals" value={crmStats.totalDeals} icon={FaHandshake} color="#10b981" />
                    <StatsCard title="Pipeline Value" value={`$${crmStats.dealsPipeline.toLocaleString()}`} icon={FaDollarSign} color="#3b82f6" />
                    <StatsCard title="Overdue Tasks" value={crmStats.tasksOverdue} icon={FaExclamationTriangle} color="#ef4444" />
                    <StatsCard title="Documents" value={crmStats.documentsCount} icon={FaFileAlt} color="#8b5cf6" />
                  </div>
                  
                  {/* Quick Actions */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                    <button className="f-button" onClick={() => setShowTaskModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Task
                    </button>
                    <button className="f-button" onClick={() => setShowDealModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Deal
                    </button>
                    <button className="f-button" onClick={() => setShowContactModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Contact
                    </button>
                    <button className="f-button" onClick={() => setShowNoteModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Note
                    </button>
                  </div>
                  
                  {/* Company Summary */}
                  <div className="f-card" style={{ padding: 20, marginBottom: 24 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FaBuilding /> Company Information
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Industry</div>
                        <div style={{ fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{selectedUser.industry || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Company Size</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{selectedUser.companySize || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Deal Stage</div>
                        <div>
                          <span style={{
                            background: selectedUser.dealStage === 'closed-won' ? '#dcfce7' : 
                                       selectedUser.dealStage === 'closed-lost' ? '#fee2e2' :
                                       selectedUser.dealStage === 'negotiation' ? '#fef3c7' :
                                       selectedUser.dealStage === 'proposal' ? '#dbeafe' :
                                       '#f3f4f6',
                            color: selectedUser.dealStage === 'closed-won' ? '#166534' :
                                   selectedUser.dealStage === 'closed-lost' ? '#991b1b' :
                                   selectedUser.dealStage === 'negotiation' ? '#92400e' :
                                   selectedUser.dealStage === 'proposal' ? '#1e40af' :
                                   '#374151',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: 'capitalize'
                          }}>
                            {(selectedUser.dealStage || 'lead').replace('-', ' ')}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Account Manager</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{selectedUser.accountManager || '—'}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Upcoming Tasks Preview */}
                  <div className="f-card" style={{ padding: 20, marginBottom: 24 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FaTasks /> Upcoming Tasks
                    </h4>
                    {tasks.filter(t => t.status !== 'completed').slice(0, 3).length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {tasks.filter(t => t.status !== 'completed').slice(0, 3).map(task => {
                          const dueDate = task.dueDate?.toDate?.() || (task.dueDate ? new Date(task.dueDate) : null);
                          const isOverdue = dueDate && dueDate < new Date();
                          return (
                            <div key={task.id} style={{ 
                              padding: 12, 
                              background: '#f9fafb', 
                              borderRadius: 6,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{task.title}</div>
                                {dueDate && (
                                  <div style={{ fontSize: 11, color: isOverdue ? '#ef4444' : 'var(--ms-text-secondary)', fontWeight: isOverdue ? 600 : 400 }}>
                                    Due: {dayjs(dueDate).format('MMM D, YYYY')} {isOverdue && '⚠️'}
                                  </div>
                                )}
                              </div>
                              <button 
                                className="f-button f-button-secondary" 
                                onClick={() => completeTask(task.id)}
                                style={{ fontSize: 11, padding: '4px 8px' }}
                              >
                                Complete
                              </button>
                            </div>
                          );
                        })}
                        <button 
                          className="f-button f-button-secondary" 
                          onClick={() => setUserTab('tasks')}
                          style={{ fontSize: 12, marginTop: 8 }}
                        >
                          View All Tasks
                        </button>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 20, color: 'var(--ms-text-secondary)' }}>
                        No upcoming tasks
                      </div>
                    )}
                  </div>
                  
                  {/* Recent Activity Preview */}
                  <div className="f-card" style={{ padding: 20 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FaChartLine /> Recent Activity
                    </h4>
                    {activityLog.slice(0, 5).length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {activityLog.slice(0, 5).map(activity => (
                          <div key={activity.id} style={{ 
                            padding: 8, 
                            background: '#f9fafb', 
                            borderRadius: 6,
                            fontSize: 12
                          }}>
                            <div>{activity.note}</div>
                            <div style={{ fontSize: 10, color: 'var(--ms-text-secondary)', marginTop: 4 }}>
                              {activity.timestamp && dayjs(activity.timestamp.toDate()).format('MMM D, h:mm A')}
                            </div>
                          </div>
                        ))}
                        <button 
                          className="f-button f-button-secondary" 
                          onClick={() => setUserTab('activity')}
                          style={{ fontSize: 12, marginTop: 8 }}
                        >
                          View Full Activity Log
                        </button>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 20, color: 'var(--ms-text-secondary)' }}>
                        No recent activity
                      </div>
                    )}
                  </div>
                </div>
              )}
              {userTab === 'crm' && !selectedUser.isBusinessAccount && (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                  <p>CRM features are only available for business accounts.</p>
                  <button className="f-button" onClick={() => {
                    setIsEditingUser(true);
                    setUserDraft(p => ({ ...p, isBusinessAccount: true }));
                    setUserTab('info');
                  }} style={{ marginTop: 16 }}>
                    Convert to Business Account
                  </button>
                </div>
              )}
              {userTab === 'reporting' && selectedUser.isBusinessAccount && (
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>CRM Reports & Analytics</h3>
                  
                  {/* Key Metrics Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
                    <div className="f-card" style={{ padding: 20, textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 8 }}>Total Deals</div>
                      <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--ms-primary)' }}>{deals.length}</div>
                    </div>
                    <div className="f-card" style={{ padding: 20, textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 8 }}>Total Pipeline</div>
                      <div style={{ fontSize: 32, fontWeight: 700, color: '#3b82f6' }}>
                        ${deals.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="f-card" style={{ padding: 20, textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 8 }}>Win Rate</div>
                      <div style={{ fontSize: 32, fontWeight: 700, color: '#10b981' }}>
                        {deals.length > 0 ? Math.round((deals.filter(d => d.stage === 'closed-won').length / deals.length) * 100) : 0}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Deal Conversion Funnel */}
                  <div className="f-card" style={{ padding: 20, marginBottom: 24 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Deal Pipeline Funnel</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {['lead', 'qualified', 'proposal', 'negotiation', 'closed-won'].map((stage, idx) => {
                        const stageDeals = deals.filter(d => d.stage === stage);
                        const stageValue = stageDeals.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
                        const maxCount = Math.max(...['lead', 'qualified', 'proposal', 'negotiation', 'closed-won'].map(s => deals.filter(d => d.stage === s).length), 1);
                        const percentage = (stageDeals.length / maxCount) * 100;
                        
                        return (
                          <div key={stage} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
                                {stage.replace('-', ' ')}
                              </span>
                              <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                                {stageDeals.length} deals • ${stageValue.toLocaleString()}
                              </span>
                            </div>
                            <div style={{ 
                              height: 24, 
                              background: '#f3f4f6', 
                              borderRadius: 4, 
                              overflow: 'hidden',
                              position: 'relative'
                            }}>
                              <div style={{ 
                                height: '100%', 
                                width: `${percentage}%`,
                                background: stage === 'closed-won' ? '#10b981' : 
                                           stage === 'negotiation' ? '#f59e0b' :
                                           stage === 'proposal' ? '#3b82f6' :
                                           stage === 'qualified' ? '#8b5cf6' :
                                           '#6b7280',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Task Completion Stats */}
                  <div className="f-card" style={{ padding: 20, marginBottom: 24 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Task Performance</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Total Tasks</div>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>{tasks.length}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Completed</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>
                          {tasks.filter(t => t.status === 'completed').length}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>Overdue</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>
                          {crmStats.tasksOverdue}
                        </div>
                      </div>
                    </div>
                    {tasks.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ 
                          height: 24, 
                          background: '#f3f4f6', 
                          borderRadius: 4, 
                          overflow: 'hidden',
                          display: 'flex'
                        }}>
                          <div style={{ 
                            height: '100%', 
                            width: `${(tasks.filter(t => t.status === 'completed').length / tasks.length) * 100}%`,
                            background: '#10b981'
                          }} />
                          <div style={{ 
                            height: '100%', 
                            width: `${(crmStats.tasksOverdue / tasks.length) * 100}%`,
                            background: '#ef4444'
                          }} />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginTop: 4, textAlign: 'center' }}>
                          {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100) : 0}% completion rate
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Activity Timeline Chart */}
                  <div className="f-card" style={{ padding: 20 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Activity Summary</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      <div style={{ textAlign: 'center', padding: 12, background: '#f9fafb', borderRadius: 6 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#3b82f6' }}>{contacts.length}</div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Contacts</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: 12, background: '#f9fafb', borderRadius: 6 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#8b5cf6' }}>{documents.length}</div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Documents</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: 12, background: '#f9fafb', borderRadius: 6 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{notes.length}</div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Notes</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: 12, background: '#f9fafb', borderRadius: 6 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{activityLog.length}</div>
                        <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Activities</div>
                      </div>
                    </div>
                    
                    {/* Recent Activity Types Breakdown */}
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--ms-border-light)' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 12 }}>
                        Activity Breakdown
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {['Task', 'Deal', 'Contact', 'Document', 'Note'].map(type => {
                          const typeActivities = activityLog.filter(a => a.note.toLowerCase().includes(type.toLowerCase()));
                          if (typeActivities.length === 0) return null;
                          return (
                            <div key={type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span>{type} Activities</span>
                              <span style={{ fontWeight: 600 }}>{typeActivities.length}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {userTab === 'reporting' && !selectedUser.isBusinessAccount && (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                  <FaChartLine style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                  <p>Reporting is only available for business accounts.</p>
                </div>
              )}
              {userTab === 'activity' && (
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Activity Log</h3>
                  
                  {/* Add Activity Form */}
                  <div style={{ marginBottom: 24, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid var(--ms-border-light)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 8 }}>Add New Activity</div>
                    <textarea 
                      className="f-input" 
                      value={newActivity} 
                      onChange={e => setNewActivity(e.target.value)}
                      rows="3" 
                      placeholder="e.g. Called client to discuss campaign requirements..."
                      style={{ fontFamily: 'inherit', resize: 'vertical', marginBottom: 12 }}
                    />
                    <button 
                      className="f-button" 
                      onClick={handleAddActivity}
                      disabled={addingActivity || !newActivity.trim()}
                    >
                      {addingActivity ? (<><FaSpinner className="fa-spin" /> Adding...</>) : 'Add Activity'}
                    </button>
                  </div>
                  
                  {/* Activity Timeline */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {activityLog.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                        <FaComment style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                        <p>No activity recorded yet</p>
                      </div>
                    ) : (
                      activityLog.map(activity => (
                        <div key={activity.id} style={{ padding: 16, background: '#fff', border: '1px solid var(--ms-border-light)', borderRadius: 8, borderLeft: '4px solid var(--ms-primary)' }}>
                          <div style={{ fontSize: 14, marginBottom: 8, whiteSpace: 'pre-wrap' }}>{activity.note}</div>
                          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                            {activity.timestamp && dayjs(activity.timestamp.toDate()).format('MMM D, YYYY [at] h:mm A')} • {activity.createdBy}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {userTab === 'tasks' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Tasks & Follow-ups</h3>
                    <button className="f-button" onClick={() => setShowTaskModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Task
                    </button>
                  </div>
                  
                  {/* Task Filters */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    {['all', 'pending', 'completed', 'overdue'].map(filter => (
                      <button 
                        key={filter}
                        className={`f-button ${taskFilter === filter ? '' : 'f-button-secondary'}`}
                        onClick={() => setTaskFilter(filter)}
                        style={{ fontSize: 12, padding: '6px 12px', textTransform: 'capitalize' }}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                  
                  {/* Task List */}
                  {tasksLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <FaSpinner className="fa-spin" />
                    </div>
                  ) : getFilteredTasks().length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {getFilteredTasks().map(task => (
                        <TaskCard 
                          key={task.id}
                          task={task}
                          onComplete={completeTask}
                          onEdit={setEditingTask}
                          onDelete={deleteTask}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                      <FaTasks style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <p>No {taskFilter !== 'all' ? taskFilter : ''} tasks found</p>
                    </div>
                  )}
                </div>
              )}
              {userTab === 'contacts' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Company Contacts</h3>
                    <button className="f-button" onClick={() => setShowContactModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Contact
                    </button>
                  </div>
                  
                  {contactsLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <FaSpinner className="fa-spin" />
                    </div>
                  ) : contacts.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {contacts.map(contact => (
                        <ContactCard 
                          key={contact.id}
                          contact={contact}
                          isPrimary={contact.isPrimary}
                          onEdit={() => setEditingContact(contact)}
                          onDelete={() => deleteContact(contact.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                      <FaUsers style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <p>No contacts added yet</p>
                    </div>
                  )}
                </div>
              )}
              {userTab === 'documents' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Documents</h3>
                    <div>
                      <input 
                        type="file" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const type = prompt('Document type (contracts/proposals/invoices/other):', 'other');
                            uploadDocument(file, { type: type || 'other' });
                          }
                          e.target.value = '';
                        }}
                        style={{ display: 'none' }}
                        id="doc-upload"
                      />
                      <label htmlFor="doc-upload" className="f-button" style={{ cursor: 'pointer' }}>
                        {uploadingDoc ? (<><FaSpinner className="fa-spin" /> Uploading...</>) : (<><FaPlus style={{ marginRight: 6 }} /> Upload Document</>)}
                      </label>
                    </div>
                  </div>
                  
                  {documentsLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <FaSpinner className="fa-spin" />
                    </div>
                  ) : documents.length > 0 ? (
                    <div>
                      {/* Document Categories */}
                      {['contracts', 'proposals', 'invoices', 'other'].map(category => {
                        const categoryDocs = documents.filter(d => (d.type || 'other').toLowerCase() === category);
                        if (categoryDocs.length === 0) return null;
                        return (
                          <div key={category} style={{ marginBottom: 24 }}>
                            <h4 style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize', marginBottom: 12, color: 'var(--ms-text-secondary)' }}>
                              {category}
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {categoryDocs.map(doc => (
                                <DocumentCard 
                                  key={doc.id}
                                  doc={doc}
                                  onView={() => window.open(doc.url, '_blank')}
                                  onDelete={deleteDocument}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                      <FaFileAlt style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <p>No documents uploaded yet</p>
                    </div>
                  )}
                </div>
              )}
              {userTab === 'deals' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Deals & Opportunities</h3>
                    <button className="f-button" onClick={() => setShowDealModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Deal
                    </button>
                  </div>
                  
                  {dealsLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <FaSpinner className="fa-spin" />
                    </div>
                  ) : deals.length > 0 ? (
                    <div>
                      {/* Deal Pipeline Board */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                        {['lead', 'qualified', 'proposal', 'negotiation', 'closed-won', 'closed-lost'].map(stage => {
                          const stageDeals = deals.filter(d => (d.stage || 'lead') === stage);
                          return (
                            <div key={stage} style={{ background: '#f9fafb', padding: 12, borderRadius: 8 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize', marginBottom: 12, color: 'var(--ms-text-secondary)' }}>
                                {stage.replace('-', ' ')} ({stageDeals.length})
                              </div>
                              <div>
                                {stageDeals.map(deal => (
                                  <DealCard 
                                    key={deal.id}
                                    deal={deal}
                                    onEdit={() => setEditingDeal(deal)}
                                    onMove={moveDealStage}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Pipeline Summary */}
                      <div className="f-card" style={{ padding: 16, marginTop: 24 }}>
                        <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Pipeline Summary</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                          <div>
                            <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Total Deals</div>
                            <div style={{ fontSize: 20, fontWeight: 700 }}>{deals.length}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Total Value</div>
                            <div style={{ fontSize: 20, fontWeight: 700 }}>
                              ${deals.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0).toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)' }}>Won Deals</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>
                              {deals.filter(d => d.stage === 'closed-won').length}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                      <FaHandshake style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <p>No deals created yet</p>
                    </div>
                  )}
                </div>
              )}
              {userTab === 'notes' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Notes</h3>
                    <button className="f-button" onClick={() => setShowNoteModal(true)}>
                      <FaPlus style={{ marginRight: 6 }} /> Add Note
                    </button>
                  </div>
                  
                  {notesLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <FaSpinner className="fa-spin" />
                    </div>
                  ) : notes.length > 0 ? (
                    <div>
                      {/* Pinned Notes */}
                      {notes.filter(n => n.isPinned).length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--ms-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FaThumbtack /> Pinned Notes
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {notes.filter(n => n.isPinned).map(note => (
                              <NoteCard 
                                key={note.id} 
                                note={note}
                                onPin={pinNote}
                                onEdit={() => setEditingNote(note)}
                                onDelete={deleteNote}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* All Notes */}
                      {notes.filter(n => !n.isPinned).length > 0 && (
                        <div>
                          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--ms-text-secondary)' }}>
                            All Notes
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {notes.filter(n => !n.isPinned).map(note => (
                              <NoteCard 
                                key={note.id} 
                                note={note}
                                onPin={pinNote}
                                onEdit={() => setEditingNote(note)}
                                onDelete={deleteNote}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                      <FaStickyNote style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <p>No notes added yet</p>
                    </div>
                  )}
                </div>
              )}
              {userTab === 'takeovers' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {userTakeovers.map(t => (
                    <div key={t.id} style={{ padding: 16, border: '1px solid var(--ms-border-light)', borderRadius: 2, cursor: 'pointer' }} onClick={() => setSelectedTakeover(t)}>
                      <div style={{ fontWeight: 600 }}>{t.signName}</div>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{new Date(t.date).toLocaleDateString()} • {t.status || 'No Status'}</div>
                    </div>
                  ))}
                </div>
              )}
              {userTab === 'campaigns' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {userCampaigns.map(c => (
                    <div key={c.id} style={{ padding: 16, border: '1px solid var(--ms-border-light)', borderRadius: 2, cursor: 'pointer' }} onClick={() => setSelectedCampaign(c)}>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{c.startDate ? new Date(c.startDate.toDate()).toLocaleDateString() : 'N/A'} • {c.status || 'No Status'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CRM MODALS */}
      
      {/* Task Modal */}
      {showTaskModal && selectedUser && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10500, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowTaskModal(false); setEditingTask(null); }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{editingTask ? 'Edit Task' : 'New Task'}</h2>
              <button onClick={() => { setShowTaskModal(false); setEditingTask(null); }} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Title *</label>
                  <input 
                    className="f-input" 
                    placeholder="e.g. Follow up on proposal"
                    defaultValue={editingTask?.title || ''}
                    id="task-title"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Description</label>
                  <textarea 
                    className="f-input" 
                    rows="3"
                    placeholder="Task details..."
                    defaultValue={editingTask?.description || ''}
                    id="task-description"
                    style={{ fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Due Date</label>
                    <input 
                      type="date"
                      className="f-input"
                      defaultValue={editingTask?.dueDate ? dayjs(editingTask.dueDate.toDate ? editingTask.dueDate.toDate() : editingTask.dueDate).format('YYYY-MM-DD') : ''}
                      id="task-dueDate"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Priority</label>
                    <select className="f-input" defaultValue={editingTask?.priority || 'medium'} id="task-priority">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Assigned To</label>
                  <input 
                    className="f-input" 
                    placeholder="e.g. John Smith"
                    defaultValue={editingTask?.assignedTo || ''}
                    id="task-assignedTo"
                  />
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="f-button f-button-secondary" onClick={() => { setShowTaskModal(false); setEditingTask(null); }}>Cancel</button>
              <button className="f-button" onClick={() => {
                const title = document.getElementById('task-title').value;
                const description = document.getElementById('task-description').value;
                const dueDate = document.getElementById('task-dueDate').value;
                const priority = document.getElementById('task-priority').value;
                const assignedTo = document.getElementById('task-assignedTo').value;
                
                if (!title) return alert('Title is required');
                
                const taskData = {
                  title,
                  description,
                  dueDate: dueDate ? new Date(dueDate) : null,
                  priority,
                  assignedTo
                };
                
                if (editingTask) {
                  updateTask(editingTask.id, taskData);
                } else {
                  addTask(taskData);
                }
              }}>
                {editingTask ? 'Update Task' : 'Create Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && selectedUser && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10500, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowContactModal(false); setEditingContact(null); }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{editingContact ? 'Edit Contact' : 'New Contact'}</h2>
              <button onClick={() => { setShowContactModal(false); setEditingContact(null); }} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Name *</label>
                  <input 
                    className="f-input" 
                    placeholder="e.g. John Smith"
                    defaultValue={editingContact?.name || ''}
                    id="contact-name"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Email *</label>
                  <input 
                    type="email"
                    className="f-input" 
                    placeholder="john@company.com"
                    defaultValue={editingContact?.email || ''}
                    id="contact-email"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Phone</label>
                  <input 
                    type="tel"
                    className="f-input" 
                    placeholder="(555) 123-4567"
                    defaultValue={editingContact?.phone || ''}
                    id="contact-phone"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Title</label>
                  <input 
                    className="f-input" 
                    placeholder="e.g. Marketing Manager"
                    defaultValue={editingContact?.title || ''}
                    id="contact-title"
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked={editingContact?.isPrimary || false} id="contact-isPrimary" />
                    Primary Contact
                  </label>
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="f-button f-button-secondary" onClick={() => { setShowContactModal(false); setEditingContact(null); }}>Cancel</button>
              <button className="f-button" onClick={() => {
                const name = document.getElementById('contact-name').value;
                const email = document.getElementById('contact-email').value;
                const phone = document.getElementById('contact-phone').value;
                const title = document.getElementById('contact-title').value;
                const isPrimary = document.getElementById('contact-isPrimary').checked;
                
                if (!name || !email) return alert('Name and email are required');
                
                const contactData = { name, email, phone, title, isPrimary };
                
                if (editingContact) {
                  updateContact(editingContact.id, contactData);
                } else {
                  addContact(contactData);
                }
              }}>
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Modal */}
      {showDealModal && selectedUser && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10500, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowDealModal(false); setEditingDeal(null); }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{editingDeal ? 'Edit Deal' : 'New Deal'}</h2>
              <button onClick={() => { setShowDealModal(false); setEditingDeal(null); }} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Deal Name *</label>
                  <input 
                    className="f-input" 
                    placeholder="e.g. Q1 Campaign"
                    defaultValue={editingDeal?.name || ''}
                    id="deal-name"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Value ($) *</label>
                  <input 
                    type="number"
                    className="f-input" 
                    placeholder="50000"
                    defaultValue={editingDeal?.value || ''}
                    id="deal-value"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Stage</label>
                    <select className="f-input" defaultValue={editingDeal?.stage || 'lead'} id="deal-stage">
                      <option value="lead">Lead</option>
                      <option value="qualified">Qualified</option>
                      <option value="proposal">Proposal</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="closed-won">Closed Won</option>
                      <option value="closed-lost">Closed Lost</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Probability (%)</label>
                    <input 
                      type="number"
                      min="0"
                      max="100"
                      className="f-input" 
                      placeholder="50"
                      defaultValue={editingDeal?.probability || ''}
                      id="deal-probability"
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Expected Close Date</label>
                  <input 
                    type="date"
                    className="f-input"
                    defaultValue={editingDeal?.expectedCloseDate ? dayjs(editingDeal.expectedCloseDate.toDate ? editingDeal.expectedCloseDate.toDate() : editingDeal.expectedCloseDate).format('YYYY-MM-DD') : ''}
                    id="deal-expectedCloseDate"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Notes</label>
                  <textarea 
                    className="f-input" 
                    rows="3"
                    placeholder="Deal notes..."
                    defaultValue={editingDeal?.notes || ''}
                    id="deal-notes"
                    style={{ fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="f-button f-button-secondary" onClick={() => { setShowDealModal(false); setEditingDeal(null); }}>Cancel</button>
              <button className="f-button" onClick={() => {
                const name = document.getElementById('deal-name').value;
                const value = document.getElementById('deal-value').value;
                const stage = document.getElementById('deal-stage').value;
                const probability = document.getElementById('deal-probability').value;
                const expectedCloseDate = document.getElementById('deal-expectedCloseDate').value;
                const notes = document.getElementById('deal-notes').value;
                
                if (!name || !value) return alert('Name and value are required');
                
                const dealData = {
                  name,
                  value: parseFloat(value),
                  stage,
                  probability: probability ? parseInt(probability) : null,
                  expectedCloseDate: expectedCloseDate ? new Date(expectedCloseDate) : null,
                  notes
                };
                
                if (editingDeal) {
                  updateDeal(editingDeal.id, dealData);
                } else {
                  addDeal(dealData);
                }
              }}>
                {editingDeal ? 'Update Deal' : 'Create Deal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && selectedUser && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10500, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowNoteModal(false); setEditingNote(null); }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{editingNote ? 'Edit Note' : 'New Note'}</h2>
              <button onClick={() => { setShowNoteModal(false); setEditingNote(null); }} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Note Content *</label>
                  <textarea 
                    className="f-input" 
                    rows="6"
                    placeholder="Write your note here..."
                    defaultValue={editingNote?.content || ''}
                    id="note-content"
                    style={{ fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Tags (comma-separated)</label>
                  <input 
                    className="f-input" 
                    placeholder="e.g. important, follow-up, meeting"
                    defaultValue={editingNote?.tags?.join(', ') || ''}
                    id="note-tags"
                  />
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="f-button f-button-secondary" onClick={() => { setShowNoteModal(false); setEditingNote(null); }}>Cancel</button>
              <button className="f-button" onClick={() => {
                const content = document.getElementById('note-content').value;
                const tagsInput = document.getElementById('note-tags').value;
                const tags = tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [];
                
                if (!content) return alert('Content is required');
                
                const noteData = { content, tags };
                
                if (editingNote) {
                  updateNote(editingNote.id, noteData);
                } else {
                  addNote(noteData);
                }
              }}>
                {editingNote ? 'Update Note' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DSP Linked Users Picker */}
      {dspLinkUsersOpen && selectedUser && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10500, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDspLinkUsersOpen(false)}>
          <div className="f-card" style={{ width: '100%', maxWidth: 720, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Link Users to DSP</div>
                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                  Selected: {dspLinkedUsersDraft.size}
                </div>
              </div>
              <button onClick={() => setDspLinkUsersOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ padding: 16, borderBottom: '1px solid var(--ms-border-light)' }}>
              <input
                className="f-input"
                placeholder="Search users by name, email, or ID…"
                value={dspLinkUserSearch}
                onChange={(e) => setDspLinkUserSearch(e.target.value)}
              />
            </div>

            <div style={{ padding: 16, overflowY: 'auto' }}>
              {users
                .filter(u => {
                  const term = (dspLinkUserSearch || '').trim().toLowerCase();
                  if (!term) return true;
                  const hay = `${u.id} ${u.name || ''} ${u.email || ''}`.toLowerCase();
                  return hay.includes(term);
                })
                .slice(0, 200)
                .map(u => (
                  <label key={u.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <input
                      type="checkbox"
                      checked={dspLinkedUsersDraft.has(u.id)}
                      onChange={() => handleToggleLinkedUser(u.id)}
                      disabled={u.id === selectedUser.id}
                      title={u.id === selectedUser.id ? 'Primary admin user is implied; do not link to itself' : 'Link this user'}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600 }}>{u.name || u.email || u.id}</span>
                      <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{u.email || '—'} • <code style={{ fontSize: 11 }}>{u.id}</code></span>
                    </div>
                  </label>
                ))}
              {users.length > 200 && (
                <div style={{ paddingTop: 10, fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                  Showing first 200 results (refine search to narrow).
                </div>
              )}
            </div>

            <div style={{ padding: 16, borderTop: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                className="f-button f-button-secondary"
                onClick={() => {
                  setDspLinkedUsersDraft(new Set());
                }}
              >
                Clear
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="f-button f-button-secondary" onClick={() => setDspLinkUsersOpen(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side Panel for Takeover Detail */}
      {selectedTakeover && (
        <div className="f-panel-overlay" style={{ zIndex: 11000 }} onClick={() => setSelectedTakeover(null)}>
          <div className="f-panel" onClick={e => e.stopPropagation()}>
            <div className="f-panel-header">
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Takeover Details</h2>
              <button onClick={() => setSelectedTakeover(null)} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>
            <TakeoverDetail takeover={selectedTakeover} refreshTakeover={fetchUsers} onClose={() => setSelectedTakeover(null)} />
          </div>
        </div>
      )}

      {/* Campaign Detail Modal */}
      {selectedCampaign && (
        <CampaignDetail campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />
      )}

      {/* DSP Creation Modal */}
      {dspCreationModalOpen && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 11000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDspCreationModalOpen(false)}>
          <div className="f-card" style={{ width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', padding: 32 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Create DSP Instance & Generate Link</h2>
              <button onClick={() => setDspCreationModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>DSP Instance Name (Agency Name) *</div>
                <input
                  className="f-input"
                  value={newDspDraft.dspInstanceName}
                  onChange={e => setNewDspDraft(prev => ({ ...prev, dspInstanceName: e.target.value }))}
                  placeholder="e.g. Elite Marketing Agency"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Website *</div>
                <input
                  className="f-input"
                  value={newDspDraft.website}
                  onChange={e => setNewDspDraft(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="e.g. elitemarketing.agency"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Tech Fee (%) *</div>
                  <input
                    className="f-input"
                    type="number"
                    value={newDspDraft.techFee}
                    onChange={e => setNewDspDraft(prev => ({ ...prev, techFee: e.target.value }))}
                    placeholder="e.g. 12.5"
                    step="0.1"
                  />
                </div>

                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Bill Terms *</div>
                  <select
                    className="f-input"
                    value={newDspDraft.billTerms}
                    onChange={e => setNewDspDraft(prev => ({ ...prev, billTerms: e.target.value }))}
                  >
                    <option value="net_30">net_30</option>
                    <option value="net_15">net_15</option>
                    <option value="pre_pay">pre_pay</option>
                  </select>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>hideTechFeeOnSignup</div>
                <select
                  className="f-input"
                  value={newDspDraft.hideTechFeeOnSignup ? 'true' : 'false'}
                  onChange={e => setNewDspDraft(prev => ({ ...prev, hideTechFeeOnSignup: e.target.value === 'true' }))}
                >
                  <option value="false">false</option>
                  <option value="true">true</option>
                </select>
                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
                  When true, the Tech Fee badge is hidden on the DSP signup page even if a tech fee is set.
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Logo *</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleUploadNewDspLogo(f);
                    }}
                    disabled={newDspLogoUploading || creatingDsp}
                  />
                  {newDspLogoUploading && <span style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}><FaSpinner className="fa-spin" /> Uploading…</span>}
                  {newDspDraft.logoUrl && !newDspLogoUploading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, color: 'var(--ms-primary)', fontWeight: 600 }}>✓ Logo uploaded</span>
                      <a href={newDspDraft.logoUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                        View
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 16, padding: 12, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#0c4a6e', lineHeight: 1.5 }}>
                  <strong>Note:</strong> After creating this DSP instance, a signup link will be generated. Share this link with the client - the first person who signs up will become the DSP admin.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => setDspCreationModalOpen(false)}
                  disabled={creatingDsp}
                >
                  Cancel
                </button>
                <button
                  className="f-button"
                  onClick={handleCreateDspAndGenerateLink}
                  disabled={creatingDsp || newDspLogoUploading}
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', border: 'none' }}
                >
                  {creatingDsp ? (
                    <><FaSpinner className="fa-spin" /> Creating…</>
                  ) : (
                    'Create & Generate Link'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DSP Link Display Modal */}
      {dspLinkModalOpen && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 11000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => {
          setDspLinkModalOpen(false);
          // Reset the draft including securityPin when closing
          setNewDspDraft({
            dspInstanceName: '',
            logoUrl: '',
            techFee: '',
            website: '',
            billTerms: 'net_30',
            securityPin: '',
            hideTechFeeOnSignup: false
          });
          // Refresh DSP instances list
          fetchAllDspInstances();
        }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 600, padding: 32 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>DSP Signup Link Generated</h2>
              <button onClick={() => {
                setDspLinkModalOpen(false);
                setNewDspDraft({
                  dspInstanceName: '',
                  logoUrl: '',
                  techFee: '',
                  website: '',
                  billTerms: 'net_30',
                  securityPin: '',
                  hideTechFeeOnSignup: false
                });
              }} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ display: 'grid', gap: 20 }}>
              <div style={{ padding: 16, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
                <div style={{ fontSize: 14, color: '#166534', fontWeight: 600, marginBottom: 4 }}>✓ DSP Instance Created Successfully!</div>
                <div style={{ fontSize: 12, color: '#166534' }}>Instance ID: <code style={{ fontFamily: 'monospace' }}>{generatedDspInstanceId}</code></div>
              </div>

              <div style={{ padding: 16, background: '#dbeafe', border: '1px solid #60a5fa', borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: 8 }}>Security PIN</div>
                <div style={{ fontSize: 13, color: '#1e3a8a', marginBottom: 8 }}>
                  Share this PIN with the client. They'll need it to complete signup.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="f-input"
                    value={newDspDraft.securityPin}
                    readOnly
                    onClick={(e) => e.target.select()}
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 24, fontWeight: 700, textAlign: 'center', letterSpacing: '0.2em', color: '#1e40af' }}
                  />
                  <button
                    className="f-button"
                    onClick={() => {
                      navigator.clipboard.writeText(newDspDraft.securityPin);
                      alert('PIN copied to clipboard!');
                    }}
                    style={{ background: '#3b82f6', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                  >
                    Copy PIN
                  </button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 8 }}>Signup Link</div>
                <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginBottom: 8 }}>
                  Share this link with the client. The first person who signs up will become the DSP admin and can invite their team.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    id="generated-dsp-link-input"
                    className="f-input"
                    value={generatedDspLink}
                    readOnly
                    onClick={(e) => e.target.select()}
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <button
                    className="f-button"
                    onClick={handleCopyDspLink}
                    style={{ background: 'var(--ms-primary)', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                  >
                    Copy Link
                  </button>
                </div>
              </div>

              <div style={{ padding: 16, background: '#fef3c7', border: '1px solid #fde047', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#854d0e', lineHeight: 1.5 }}>
                  <strong>Important:</strong> Save both the PIN and link! The client will need the PIN to complete signup. The first user to sign up will automatically become the DSP admin.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => {
                    setDspLinkModalOpen(false);
                    setNewDspDraft({
                      dspInstanceName: '',
                      logoUrl: '',
                      techFee: '',
                      website: '',
                      billTerms: 'net_30',
                      securityPin: '',
                      hideTechFeeOnSignup: false
                    });
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function formatBool(v) { return v ? <span style={{color:'#107c10',fontWeight:600}}>Yes</span> : <span style={{color:'#d13438',fontWeight:600}}>No</span>; }

function getInitials(n, e) {
  if (n) { const p = n.split(' '); return (p[0][0] + (p[1]?.[0] || '')).toUpperCase(); }
  return (e || '?')[0].toUpperCase();
}

export default UsersList;
